/**
 * Shared stage library — reusable equipment/instrument templates keyed by a
 * stable stageKey. Archetypes (./archetypes.ts) compose plants by referencing
 * these keys. Each template is an EntityDef the generator clones into one node
 * per instance.
 *
 * Archetype A (iron green-sand) reuses the canonical foundry entity defs from
 * foundry-widgets/foundry-plant.ts verbatim, so a default generatePlant()
 * reproduces the existing 15-station plant. Archetypes B–H add their own stage
 * templates below (EAF/AOD/cupola/crucible melt variants, Mg treatment,
 * inoculation, no-bake moulding + reclamation, cold-box cores, HPDC/gravity/LPDC
 * die-casting, investment wax+shell, finishing, heat-treat, machining, NDT lab).
 *
 * Domain grounding: docs/design/foundry-industry-study.md §3 (flows) + §4
 * (per-stage parameters with units/ranges, QC tests, defects). Material classes
 * are mapped onto the fixed `MaterialClass` union — any molten metal →
 * molten_iron (recoloured per-metal by generatePlant), casting → raw_casting (in)
 * / finished_casting (accepted), charge → ferrous_charge | alloy_material, etc.
 * The consumed-mould / secondary-output class is per-ROUTE (cosmetic-correctness):
 * green-sand → green_sand_mould, no-bake (incl. its cores) → no_bake_mould,
 * gravity/LPDC die insert → permanent_mould, investment shell → investment_shell.
 * Die-casting (HPDC) has NO consumed mould (the steel die is permanent equipment).
 * (Al/steel/Cu MOLTEN edges still render via the per-metal molten class.)
 */

import type {
  EntityDef,
  ParameterDef,
  PortDef,
} from "../../../foundry-widgets/foundry-plant";
import {
  ENT_CHARGE_PREP,
  ENT_COOLING,
  ENT_FURNACE,
  ENT_MOISTURE_TESTER,
  ENT_MOULDING,
  ENT_POUR,
  ENT_QC,
  ENT_SAND_MIXER,
  ENT_SHAKEOUT,
  ENT_SPECTROMETER,
  ENT_TENSILE,
  ENT_VCCD,
  ENT_VMBD,
  ENT_VMHD,
  ENT_VPD,
  ENT_VUD,
} from "../../../foundry-widgets/foundry-plant";
import type { StageTemplate } from "./types";

// ── New stage entity defs (B–H) ──────────────────────────────────────────────
// Authored here (not in foundry-plant.ts) since only the catalog files change.
// Generic material mapping per the hard-constraint note above.

// --- Melt variants -----------------------------------------------------------

/** Cupola melt — coke/metal bed, hot-blast; high-S pickup risk (iron). §4 cupola. */
const ENT_CUPOLA: EntityDef = {
  id: "ent-cupola",
  templateId: "foundry.cupola_furnace",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "A coke-fired shaft furnace that melts the charge to molten iron.",
  flowMode: "continuous",
  // WS-B#8 (2026-08-02): the rated `melt_rate 10 t/h` was DELETED. It was not a
  // registry key, so it reached no reader — and the SAME number, same unit, is
  // already the streamed `melt_rate` parameter below (the live home: parameters
  // become gateway tags). Two homes for one quantity, one of them dead. Dossier
  // 01-melt-line.md:482 rules melt_rate is "a real key or leave as correlation";
  // the parameter is the correlation home.
  ratedProperties: [
    { name: "capacity_kg", value: 10000, unit: "kg" },
    { name: "melt_loss_pct", value: 4.5, unit: "%" },
  ],
  inputPorts: [{ id: "in_charge", name: "furnace_charge", materialClass: "ferrous_charge", flowMode: "continuous" }],
  outputPorts: [
    { id: "out_metal", name: "molten_metal", materialClass: "molten_iron", flowMode: "continuous" },
    { id: "out_slag", name: "slag", materialClass: "waste" },
  ],
  parameters: [
    { name: "tap_temperature", unit: "°C", value: 1450, range: [1400, 1500], dataKind: "analog_scalar" },
    { name: "metal_coke_ratio", unit: ":1", value: 10, range: [8, 12], dataKind: "analog_scalar" },
    { name: "blast_air", unit: "Nm³/min", value: 90, range: [60, 130], dataKind: "analog_scalar" },
    { name: "melt_rate", unit: "t/h", value: 10, range: [6, 14], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_tap", name: "tap", purpose: "pulse", dataKind: "count_scalar" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "",
    label: "Heat",
    isPart: false,
    qc: { tests: ["carbon", "carbon_equivalent"], defects: ["off_chemistry", "high_sulfur"] },
  },
};

/** EAF — electric-arc steel melt; high tap temp, feeds ladle/AOD. §4 EAF+AOD. */
const ENT_EAF: EntityDef = {
  id: "ent-eaf",
  templateId: "foundry.electric_arc_furnace",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "An electric arc furnace that melts a charge of steel scrap under graphite arcs.",
  flowMode: "batch",
  ratedProperties: [
    { name: "capacity_kg", value: 8000, unit: "kg" },
    { name: "rated_power_kw", value: 6000, unit: "kW" },
    { name: "melt_loss_pct", value: 5.0, unit: "%" },
    { name: "melt_time_min", value: 70, unit: "min" },
  ],
  inputPorts: [{ id: "in_charge", name: "steel_charge", materialClass: "ferrous_charge", flowMode: "batch" }],
  outputPorts: [
    { id: "out_metal", name: "molten_steel", materialClass: "molten_iron", flowMode: "batch" },
    { id: "out_slag", name: "slag", materialClass: "waste" },
  ],
  parameters: [
    { name: "tap_temperature", unit: "°C", value: 1640, range: [1600, 1680], dataKind: "analog_scalar" },
    { name: "power_kw", unit: "kW", value: 5200, range: [3000, 6000], dataKind: "analog_scalar" },
    { name: "tap_to_tap", unit: "min", value: 70, range: [50, 110], dataKind: "analog_scalar" },
    { name: "carbon", unit: "%", value: 0.4, range: [0.1, 0.6], dataKind: "analog_percentage" },
  ],
  boundarySignals: [
    { id: "sig_melt_start", name: "melt_start", purpose: "cycle_start", dataKind: "boolean_state" },
    { id: "sig_tap", name: "tap", purpose: "cycle_end", dataKind: "boolean_state" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "",
    label: "Heat",
    isPart: false,
    qc: { tests: ["carbon", "chemistry"], defects: ["off_chemistry", "inclusions"] },
  },
};

/** AOD/ladle refining — decarb + chemistry trim for low-C stainless. §4 EAF+AOD. */
const ENT_AOD: EntityDef = {
  id: "ent-aod",
  templateId: "foundry.aod_converter",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "An argon oxygen decarburisation (AOD) converter that refines the steel to low carbon.",
  flowMode: "batch",
  ratedProperties: [{ name: "capacity_kg", value: 6000, unit: "kg" }],
  inputPorts: [{ id: "in_metal", name: "molten_steel", materialClass: "molten_iron", flowMode: "batch" }],
  outputPorts: [
    { id: "out_metal", name: "refined_steel", materialClass: "molten_iron", flowMode: "batch" },
    { id: "out_slag", name: "slag", materialClass: "waste" },
  ],
  parameters: [
    { name: "treat_temperature", unit: "°C", value: 1670, range: [1620, 1720], dataKind: "analog_scalar" },
    { name: "o2_ar_ratio", unit: ":1", value: 2, range: [0.5, 4], dataKind: "analog_scalar" },
    { name: "decarb_endpoint_c", unit: "%", value: 0.03, range: [0.01, 0.08], dataKind: "analog_percentage" },
  ],
  boundarySignals: [{ id: "sig_refine_done", name: "refine_complete", purpose: "cycle_end", dataKind: "boolean_state" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "",
    label: "Heat",
    isPart: false,
    qc: { tests: ["carbon", "chromium", "chemistry"], defects: ["off_chemistry", "inclusions"] },
  },
};

/** Crucible / gas-fired furnace — non-ferrous (Al, Cu, Zn/Mg pot). §1 melt units. */
const ENT_CRUCIBLE: EntityDef = {
  id: "ent-crucible",
  templateId: "foundry.crucible_furnace",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "A crucible or gas-fired furnace that melts a non-ferrous charge.",
  flowMode: "batch",
  ratedProperties: [
    { name: "capacity_kg", value: 500, unit: "kg" },
    { name: "rated_power_kw", value: 120, unit: "kW" },
    { name: "melt_loss_pct", value: 2.0, unit: "%" },
  ],
  inputPorts: [{ id: "in_charge", name: "metal_charge", materialClass: "alloy_material", flowMode: "batch" }],
  outputPorts: [
    { id: "out_metal", name: "molten_metal", materialClass: "molten_iron", flowMode: "batch" },
    { id: "out_dross", name: "dross", materialClass: "waste" },
  ],
  parameters: [
    { name: "bath_temperature", unit: "°C", value: 720, range: [680, 780], dataKind: "analog_scalar" },
    { name: "hold_time", unit: "min", value: 20, range: [10, 40], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_tap", name: "tap", purpose: "cycle_end", dataKind: "boolean_state" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "",
    label: "Heat",
    isPart: false,
    qc: { tests: ["chemistry"], defects: ["off_chemistry", "dross"] },
  },
};

/** Reverb / dosing melt — bulk Al melting + metered dose to HPDC. §1/§4 HPDC. */
const ENT_REVERB: EntityDef = {
  id: "ent-reverb",
  templateId: "foundry.reverb_melting_furnace",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "A reverberatory furnace that melts aluminium in bulk and doses it for casting.",
  flowMode: "continuous",
  // WS-B#8: rated `melt_rate 1500 kg/h` deleted — same duplication as the cupola
  // (the identical number is the streamed parameter below).
  ratedProperties: [{ name: "capacity_kg", value: 5000, unit: "kg" }],
  inputPorts: [{ id: "in_charge", name: "aluminium_charge", materialClass: "alloy_material", flowMode: "continuous" }],
  outputPorts: [
    { id: "out_metal", name: "molten_aluminium", materialClass: "molten_iron", flowMode: "continuous" },
    { id: "out_dross", name: "dross", materialClass: "waste" },
  ],
  parameters: [
    { name: "bath_temperature", unit: "°C", value: 730, range: [700, 760], dataKind: "analog_scalar" },
    { name: "melt_rate", unit: "kg/h", value: 1500, range: [800, 2200], dataKind: "analog_scalar" },
    { name: "dross_rate", unit: "%", value: 2, range: [1, 4], dataKind: "analog_percentage" },
  ],
  boundarySignals: [{ id: "sig_tap", name: "metal_ready", purpose: "cycle_end", dataKind: "boolean_state" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "",
    label: "Heat",
    isPart: false,
    qc: { tests: ["chemistry"], defects: ["off_chemistry", "dross"] },
  },
};

/** Holding furnace — superheat/buffer between melt and pour/treatment. §3 step 4. */
const ENT_HOLDING: EntityDef = {
  id: "ent-holding",
  templateId: "foundry.holding_furnace",
  class: "EQUIPMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "flame",
  oneLine: "A ladle or holding furnace that keeps the tapped metal hot until it is poured.",
  flowMode: "continuous",
  ratedProperties: [
    { name: "capacity_kg", value: 3000, unit: "kg" },
    { name: "rated_power_kw", value: 200, unit: "kW" },
  ],
  inputPorts: [{ id: "in_metal", name: "molten_metal", materialClass: "molten_iron", flowMode: "continuous" }],
  outputPorts: [{ id: "out_metal", name: "held_metal", materialClass: "molten_iron", flowMode: "continuous" }],
  parameters: [
    { name: "hold_temperature", unit: "°C", value: 1620, range: [1580, 1680], dataKind: "analog_scalar" },
    { name: "residence_time", unit: "min", value: 30, range: [10, 90], dataKind: "analog_scalar" },
  ],
};

// --- Iron treatment ----------------------------------------------------------

/** Mg treatment (ductile) — nodularisation; residual Mg + fade risk. §4 Mg treat. */
const ENT_MG_TREATMENT: EntityDef = {
  id: "ent-mg-treatment",
  templateId: "foundry.mg_treatment_station",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "Treats the iron with a ferrosilicon magnesium alloy, in a sandwich or tundish ladle, so its graphite forms nodules.",
  flowMode: "batch",
  // WS-D INV-M1: the STANDING RATE that sizes the treatment addition, and the one
  // the emitter reads (alloy_additions.go:planAlloyAdditions). It is deliberately a
  // RATED property and not the `fesimg_addition` data port below: the port is what
  // the operation REPORTS for this heat (now derived from the alloy lot the emitter
  // mints), the rating is what the shop STANDS ON. The key names the ALLOY percent,
  // never the Mg element percent — the two differ by ~20x.
  // WS-B#9b (2026-08-03) — the dossier's two treatment-pot numbers, authored from
  // 01-melt-line.md:293 ("Ladle capacity | kg | 1500 [500-5000]") and :294 ("Mg →
  // pour window | s | 360-600 (STD 600)"). Both are DOSSIER-STATED defaults, not
  // midpoints of a band: the capacity row names 1500 and the window row names an
  // explicit STD. Both are also genuinely READ — the pot capacity by the treatment
  // -grain resolver (treatment_grain.go:206, via ratedKgOr so a non-kg unit is
  // refused, not coerced) and the window by the Mg-fade model — so they are the
  // rare authoring rows that are WIRED rather than nameplate.
  ratedProperties: [
    { name: "nodulariser_addition_pct", value: 1.4, unit: "%" },
    // NOTE the dossier's brackets ([500-5000] kg, [360-600] s) are NOT carried
    // here: `RatedProperty` is {name, value, unit?} with no `range` (unlike
    // `parameters`), so a validated band has nowhere to live on a rating today.
    // Stated rather than silently dropped — see the WS-B#9b block in _STATUS.
    { name: "capacity_kg", value: 1500, unit: "kg" },
    { name: "mg_window_sec", value: 600, unit: "s" },
  ],
  inputPorts: [
    { id: "in_metal", name: "base_metal", materialClass: "molten_iron", flowMode: "batch" },
    { id: "in_fesimg", name: "fesimg_alloy", materialClass: "alloy_material" },
  ],
  outputPorts: [{ id: "out_metal", name: "treated_metal", materialClass: "molten_iron", flowMode: "batch" }],
  parameters: [
    { name: "treat_temperature", unit: "°C", value: 1465, range: [1450, 1480], dataKind: "analog_scalar", paramClass: "temperature" },
    { name: "fesimg_addition", unit: "%", value: 1.4, range: [1.0, 1.8], dataKind: "analog_percentage" },
    { name: "mg_recovery", unit: "%", value: 55, range: [40, 65], dataKind: "analog_percentage" },
    // WS-D / MET §3: residual Mg is a per-LADLE MEASURED outcome (spectro / thermal analysis on
    // the treated ladle), so the PORT is right and the nominal was not: a shipped 0.042 % is a
    // measurement nobody made, and it is the number `plantParamValueEx` drew around — the emitter
    // rendered a fabricated residual Mg on the treatment op of every heat. Range kept as the
    // operating window; value deleted. Same disposition WS-B#2 gave the charge weighbridge.
    { name: "residual_mg", unit: "%", range: [0.035, 0.05], dataKind: "analog_percentage", paramClass: "chemistry" },
  ],
  boundarySignals: [{ id: "sig_treat_done", name: "treatment_complete", purpose: "cycle_end", dataKind: "boolean_state" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "",
    label: "Heat",
    isPart: false,
    qc: { tests: ["nodularity", "nodule_count", "residual_mg"], defects: ["low_nodularity", "mg_fade", "dross"] },
  },
};

/** Inoculation — late FeSi addition; controls chill/carbides. §4 inoculation. */
const ENT_INOCULATION: EntityDef = {
  id: "ent-inoculation",
  templateId: "foundry.inoculation_station",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "Adds a ferrosilicon inoculant late, which promotes graphite and prevents chill.",
  flowMode: "batch",
  // WS-D INV-M1: the standing inoculant rate the emitter sizes the FeSi lot from.
  // WS-B#9b: the pot capacity, dossier 01-melt-line.md:342 ("Ladle capacity | kg |
  // 1500 [200-5000]"). 1500 is the dossier's stated value AND the value all four
  // inoculation ladles already persisted on staging carry, so this authoring makes
  // the template agree with the plants rather than introducing a new number.
  ratedProperties: [
    { name: "inoculant_addition_pct", value: 0.3, unit: "%" },
    { name: "capacity_kg", value: 1500, unit: "kg" },
  ],
  inputPorts: [
    { id: "in_metal", name: "treated_metal", materialClass: "molten_iron", flowMode: "batch" },
    { id: "in_inoculant", name: "inoculant", materialClass: "alloy_material" },
  ],
  outputPorts: [{ id: "out_metal", name: "inoculated_metal", materialClass: "molten_iron", flowMode: "batch" }],
  parameters: [
    { name: "inoculant_addition", unit: "%", value: 0.3, range: [0.1, 0.6], dataKind: "analog_percentage" },
    { name: "time_after_treat", unit: "min", value: 4, range: [1, 10], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_inoc_done", name: "inoculation_complete", purpose: "cycle_end", dataKind: "boolean_state" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "",
    label: "Heat",
    isPart: false,
    qc: { tests: ["chill_wedge", "nodule_count"], defects: ["chill_carbides", "shrinkage"] },
  },
};

// --- Aluminium melt treatment ------------------------------------------------

/** Degassing — rotary degas removes H₂; Sr modify / Ti-B grain refine. §4 gravity/LPDC. */
const ENT_DEGASSING: EntityDef = {
  id: "ent-degassing",
  templateId: "foundry.degassing_station",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "A rotary degasser that purges the dissolved hydrogen from the aluminium melt with argon or nitrogen.",
  flowMode: "batch",
  inputPorts: [
    { id: "in_metal", name: "molten_aluminium", materialClass: "molten_iron", flowMode: "batch" },
    { id: "in_treatment", name: "modifier_grain_refiner", materialClass: "alloy_material" },
  ],
  outputPorts: [{ id: "out_metal", name: "degassed_metal", materialClass: "molten_iron", flowMode: "batch" }],
  parameters: [
    { name: "melt_temperature", unit: "°C", value: 735, range: [710, 760], dataKind: "analog_scalar" },
    { name: "rotor_speed", unit: "rpm", value: 400, range: [300, 550], dataKind: "analog_scalar" },
    { name: "degas_time", unit: "min", value: 8, range: [4, 15], dataKind: "analog_scalar" },
    { name: "hydrogen_content", unit: "ml/100g", value: 0.15, range: [0.05, 0.35], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_degas_done", name: "degas_complete", purpose: "cycle_end", dataKind: "boolean_state" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "",
    label: "Heat",
    isPart: false,
    qc: { tests: ["reduced_pressure_test", "hydrogen"], defects: ["hydrogen_porosity", "oxide_bifilm"] },
  },
};

// --- No-bake sand route ------------------------------------------------------

/** No-bake mixer — sand + resin + catalyst, self-set at RT. §4 moulding no-bake. */
/** Continuous no-bake mixer — silica sand + resin + catalyst + reclaimed sand
 *  → self-setting sand (#699). `in_silica`/`in_return` were already correctly
 *  classed but never wired in any archetype (only the reclaimed-sand loop
 *  was); `in_resin` was ALSO declared but classed `alloy_material` (a metal
 *  charge-additive class, not a binder one) and likewise unwired. Both bugs
 *  fixed together: the binder port is split into its real resin + catalyst
 *  identities (`no_bake_resin` / `no_bake_catalyst`, #699) and every fresh
 *  port now has a real source — see NOBAKE_SUPPLY_YARD_STAGE below. */
const ENT_NOBAKE_MIXER: EntityDef = {
  id: "ent-nobake-mixer",
  templateId: "foundry.nobake_continuous_mixer",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "A continuous mixer that blends silica sand, resin and catalyst into self-setting sand.",
  flowMode: "continuous",
  ratedProperties: [
    { name: "flow_rate_t_per_h", value: 20, unit: "t/h" },
    { name: "bench_life_min", value: 30, unit: "min" },
    // WS-B#9b: dossier 02-sand-core.md:267 ("Rated motor power | kW | 22 [11-75]")
    // — a dossier-STATED 22, not a band midpoint. NAMEPLATE_CONTROL: installed
    // power reaches no heat model, and the panel now says so via WireClassChip.
    { name: "rated_power_kw", value: 22, unit: "kW" },
  ],
  inputPorts: [
    { id: "in_silica", name: "silica_sand", materialClass: "silica_sand" },
    { id: "in_return", name: "reclaimed_sand", materialClass: "return_sand" },
    { id: "in_resin", name: "resin", materialClass: "no_bake_resin" },
    { id: "in_catalyst", name: "catalyst", materialClass: "no_bake_catalyst" },
  ],
  outputPorts: [{ id: "out_sand", name: "nobake_sand", materialClass: "green_sand" }],
  parameters: [
    { name: "resin_addition", unit: "%", value: 1.0, range: [0.8, 1.2], dataKind: "analog_percentage" },
    { name: "catalyst_addition", unit: "% of resin", value: 45, range: [35, 60], dataKind: "analog_percentage" },
    { name: "mix_rate", unit: "t/h", value: 20, range: [10, 30], dataKind: "analog_scalar" },
    { name: "sand_temperature", unit: "°C", value: 28, range: [20, 35], dataKind: "analog_scalar" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "",
    label: "Sand batch",
    isPart: false,
    qc: { tests: ["tensile_strength", "work_time"], defects: ["uncured", "low_strength"] },
  },
};

/** No-bake moulding bay — mould+strip; work/strip time. §4 moulding no-bake. */
const ENT_NOBAKE_MOULD: EntityDef = {
  id: "ent-nobake-mould",
  templateId: "foundry.nobake_moulding_bay",
  class: "EQUIPMENT",
  lotOp: "SPLIT",
  iconKey: "cog",
  oneLine: "Packs self-setting sand around the pattern and strips the mould once it sets.",
  inputPorts: [
    { id: "in_sand", name: "nobake_sand", materialClass: "green_sand" },
    { id: "in_pattern", name: "pattern", materialClass: "pattern" },
    { id: "in_core", name: "core", materialClass: "no_bake_mould" },
  ],
  outputPorts: [{ id: "out_moulds", name: "moulds", materialClass: "no_bake_mould" }],
  parameters: [
    { name: "work_time", unit: "min", value: 8, range: [3, 20], dataKind: "analog_scalar" },
    { name: "strip_time", unit: "min", value: 30, range: [15, 90], dataKind: "analog_scalar" },
    { name: "mould_hardness", unit: "AFS", value: 88, range: [80, 95], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_mould_made", name: "mould_made", purpose: "pulse", dataKind: "count_scalar" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "MO",
    label: "Mould",
    isPart: true,
    qc: { tests: ["mould_hardness", "coating"], defects: ["veining", "penetration", "gas_defect"] },
  },
};

/** Mould coat + dry — refractory wash + oven/torch dry. §4 moulding no-bake. */
const ENT_MOULD_COAT: EntityDef = {
  id: "ent-mould-coat",
  templateId: "foundry.mould_coat_dry",
  class: "EQUIPMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "cog",
  oneLine: "Coats the mould with a refractory wash and dries it in an oven or with a torch.",
  inputPorts: [{ id: "in_mould", name: "moulds", materialClass: "no_bake_mould" }],
  outputPorts: [{ id: "out_mould", name: "coated_moulds", materialClass: "no_bake_mould" }],
  parameters: [
    { name: "coating_thickness", unit: "mm", value: 0.4, range: [0.2, 0.8], dataKind: "analog_scalar" },
    { name: "dry_time", unit: "min", value: 25, range: [10, 60], dataKind: "analog_scalar" },
    { name: "dry_temperature", unit: "°C", value: 150, range: [80, 200], dataKind: "analog_scalar" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "MO",
    label: "Mould",
    isPart: false,
    qc: { tests: ["coating", "moisture"], defects: ["uncured", "penetration"] },
  },
};

/** Sand reclamation — mechanical/thermal recovery 90–95% → mixer. §2/§5 reclaim.
 *  reclaim_yield_pct 70 means ~30% of spent sand entering this stage is lost
 *  to attrition (out_dust), never coming back around as return_sand — a real
 *  mass deficit the loop needs made up SOMEWHERE, not just at the mixer's own
 *  in_silica (#699). `in_makeup` is that make-up, a small fresh-silica
 *  top-up co-fed with the spent sand so the reclaimed output holds steady;
 *  see NOBAKE_SUPPLY_YARD_STAGE below for the source. */
const ENT_SAND_RECLAIM: EntityDef = {
  id: "ent-sand-reclaim",
  templateId: "foundry.sand_reclamation",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Strips the binder off used sand by attrition or heat and returns the sand to the mixer.",
  flowMode: "continuous",
  ratedProperties: [
    { name: "flow_rate_t_per_h", value: 20, unit: "t/h" },
    { name: "reclaim_yield_pct", value: 70, unit: "%" },
  ],
  inputPorts: [
    { id: "in_spent", name: "spent_sand", materialClass: "return_sand" },
    { id: "in_makeup", name: "fresh_makeup_sand", materialClass: "silica_sand" },
  ],
  outputPorts: [
    { id: "out_reclaimed", name: "reclaimed_sand", materialClass: "return_sand" },
    { id: "out_dust", name: "dust_fines", materialClass: "waste" },
  ],
  // WS-B#8: the duplicate `reclamation_yield 92%` parameter was DELETED. This
  // entity declared the SAME quantity twice with two different numbers — a rated
  // `reclaim_yield_pct 70` and a 92% parameter — and both were dead. The registry
  // key is the one home (dossier 02-sand-core.md:322 assigns reclaim_yield_pct,
  // and :372 calls the 92% a [DEFAULT] that must become weighed).
  // The VALUE 70 is left as shipped, deliberately: the dossier bands are per
  // reclaim TYPE (mech green-sand 60-80 · mech no-bake 75-90 · thermal 95-98) and
  // this template is "attrition/thermal", so which band applies is a dossier-lock
  // decision, not a number to invent here.
  parameters: [
    { name: "loi", unit: "%", value: 1.5, range: [0.5, 3], dataKind: "analog_percentage" },
    { name: "reclaim_temperature", unit: "°C", value: 120, range: [40, 750], dataKind: "analog_scalar" },
  ],
};

/** No-bake supply yard — purchase boundary issuing fresh silica sand, no-bake
 *  resin and catalyst (#699). A source/storage stage like ENT_STD_CHARGE_YARD:
 *  no input ports, so its outputs are grounded as bought/consumed material.
 *  Shared by every no_bake-route archetype (ductile, steel, copper-base) —
 *  same fresh-material boundary, feeding both the mixer's fresh charge and
 *  the reclaimer's make-up for its ~30% attrition loss (see ENT_SAND_RECLAIM's
 *  reclaim_yield_pct above). */
const ENT_NOBAKE_SUPPLY_YARD: EntityDef = {
  id: "ent-nobake-supply-yard",
  templateId: "foundry.nobake_supply_yard",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "The purchase point for the fresh silica sand, resin and catalyst of the no-bake line.",
  inputPorts: [],
  outputPorts: [
    { id: "out_silica", name: "silica_sand", materialClass: "silica_sand" },
    { id: "out_resin", name: "no_bake_resin", materialClass: "no_bake_resin" },
    { id: "out_catalyst", name: "no_bake_catalyst", materialClass: "no_bake_catalyst" },
  ],
  parameters: [
    { name: "silica_stock", unit: "t", value: 60, range: [0, 150], dataKind: "analog_scalar" },
    { name: "resin_stock", unit: "t", value: 5, range: [0, 20], dataKind: "analog_scalar" },
  ],
};

// --- Core shop ---------------------------------------------------------------

/** Cold-box core machine — gas-cured cores; core gas → blowholes. §4 core making. */
const ENT_CORE: EntityDef = {
  id: "ent-core",
  templateId: "foundry.cold_box_core_machine",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Blows resin-bonded sand into the core box and cures each core with amine gas.",
  flowMode: "batch",
  // WS-B#9b: dossier 02-sand-core.md:638 ("Cores per cycle (box cavities) | count |
  // 1-8 | add cavities_per_box; prior 4"). The 4 is the dossier's own quoted PRIOR,
  // and the sibling foundry.std_core_shooter has shipped 4 since the catalog was
  // written — so this is a stated value, not a band midpoint invented here.
  //
  // Unit-less on purpose: it is a COUNT, and `capacity_kg`'s history (WS-B#7) is what
  // happens when a count travels under a mass unit. NAMEPLATE_CONTROL — cavities per
  // SHOT of this core box, which is NOT the retired `cores_per_mould` (cores a mould
  // receives, now derived from the pattern↔core-box link table). The two multiply.
  ratedProperties: [{ name: "cavities_per_box", value: 4 }],
  inputPorts: [
    { id: "in_sand", name: "core_sand", materialClass: "silica_sand" },
    { id: "in_binder", name: "binder", materialClass: "alloy_material" },
  ],
  outputPorts: [{ id: "out_cores", name: "cores", materialClass: "no_bake_mould" }],
  parameters: [
    { name: "binder_addition", unit: "%", value: 1.4, range: [0.8, 2.5], dataKind: "analog_percentage" },
    { name: "gas_time", unit: "s", value: 15, range: [5, 30], dataKind: "analog_scalar" },
    { name: "blow_pressure", unit: "bar", value: 4, range: [2, 6], dataKind: "analog_scalar" },
    { name: "core_hardness", unit: "AFS", value: 85, range: [70, 95], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_core_made", name: "core_made", purpose: "pulse", dataKind: "count_scalar" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "CR",
    label: "Core",
    isPart: true,
    qc: { tests: ["core_hardness", "dimensional", "gas_evolution"], defects: ["blowholes", "breakage", "veining"] },
  },
};

// --- Die casting (HPDC / gravity / LPDC) -------------------------------------

/** HPDC machine — high-pressure injection into steel die. §4 HPDC. */
const ENT_HPDC: EntityDef = {
  id: "ent-hpdc",
  templateId: "foundry.hpdc_machine",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "A high pressure die casting machine that injects molten metal into a steel die.",
  flowMode: "discrete_unit",
  // WS-B#8 (2026-08-02): this template shipped TWO dead rated names.
  //   · `cycle_time_s 40 s` — deleted: not a registry key, and the identical
  //     number already lives as the `cycle_time` parameter below. Dossier
  //     04-finish-ht.md:565 rules cycle_time is a cadence value and says
  //     "dedupe with the identically-named param".
  //   · `locking_force 800 t` — MOVED to parameters[]. It is a real HPDC value
  //     with no registry key and no duplicate, so deleting it would lose a true
  //     machine fact; and clamp/locking force is genuinely monitored per shot on
  //     a real machine, exactly like its neighbours (intensification pressure,
  //     plunger velocity), so a data port is its honest home.
  inputPorts: [{ id: "in_metal", name: "molten_metal", materialClass: "molten_iron", flowMode: "continuous" }],
  outputPorts: [{ id: "out_castings", name: "shots", materialClass: "raw_casting", flowMode: "discrete_unit" }],
  parameters: [
    { name: "locking_force", unit: "t", value: 800, range: [400, 1600], dataKind: "analog_scalar" },
    { name: "intensification_pressure", unit: "MPa", value: 60, range: [10, 100], dataKind: "analog_scalar" },
    { name: "plunger_velocity", unit: "m/s", value: 3, range: [2, 6], dataKind: "analog_scalar" },
    { name: "gate_velocity", unit: "m/s", value: 40, range: [30, 60], dataKind: "analog_scalar" },
    { name: "die_temperature", unit: "°C", value: 220, range: [180, 280], dataKind: "analog_scalar" },
    { name: "melt_temperature", unit: "°C", value: 670, range: [660, 700], dataKind: "analog_scalar" },
    { name: "vacuum_level", unit: "mbar", value: 80, range: [40, 150], dataKind: "analog_scalar" },
    { name: "cycle_time", unit: "s", value: 40, range: [20, 60], dataKind: "analog_scalar" },
  ],
  boundarySignals: [
    { id: "sig_shot", name: "shot_made", purpose: "pulse", dataKind: "count_scalar" },
    { id: "sig_cycle_start", name: "cycle_start", purpose: "cycle_start", dataKind: "boolean_state" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "CA",
    label: "Casting",
    isPart: true,
    qc: { tests: ["xray", "dimensional", "leak"], defects: ["gas_porosity", "shrinkage", "cold_shut", "flash", "soldering"] },
  },
};

/** Die spray — die-lube spray + air blow between HPDC shots. §4 HPDC (spray). */
const ENT_DIE_SPRAY: EntityDef = {
  id: "ent-die-spray",
  templateId: "foundry.die_spray_unit",
  class: "EQUIPMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "cog",
  oneLine: "Sprays release lubricant on the die and blows it clean with air each cycle.",
  inputPorts: [{ id: "in_castings", name: "shots", materialClass: "raw_casting" }],
  outputPorts: [{ id: "out_castings", name: "shots", materialClass: "raw_casting" }],
  parameters: [
    { name: "spray_time", unit: "s", value: 2.5, range: [1, 5], dataKind: "analog_scalar" },
    { name: "lube_dilution", unit: ":1", value: 80, range: [40, 150], dataKind: "analog_scalar" },
  ],
};

/** Trim press — shears overflow/runner/flash from HPDC shot. §4 HPDC (trim). */
const ENT_TRIM_PRESS: EntityDef = {
  id: "ent-trim-press",
  templateId: "foundry.trim_press",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Shears the flash, runners and overflows off the casting.",
  flowMode: "discrete_unit",
  inputPorts: [{ id: "in_castings", name: "shots", materialClass: "raw_casting" }],
  outputPorts: [{ id: "out_castings", name: "trimmed_castings", materialClass: "raw_casting" }],
  parameters: [
    { name: "press_force", unit: "t", value: 60, range: [20, 120], dataKind: "analog_scalar" },
    { name: "cycle_time", unit: "s", value: 6, range: [3, 12], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_trim", name: "part_trimmed", purpose: "pulse", dataKind: "count_scalar" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "CA",
    label: "Casting",
    isPart: true,
    qc: { tests: ["dimensional", "visual"], defects: ["flash", "broken_part"] },
  },
};

/** Gravity-die / LPDC station — reusable die, gravity or low-pressure fill. §4 gravity/LPDC. */
const ENT_GRAVITY_DIE: EntityDef = {
  id: "ent-gravity-die",
  templateId: "foundry.gravity_lpdc_station",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "A gravity or low pressure die station that fills a reusable steel die each cycle.",
  flowMode: "discrete_unit",
  // WS-B#8: rated `cycle_time_s 180 s` deleted — dead name, and the identical
  // number is the streamed `cycle_time` parameter below (dossier 04:565 dedupe).
  inputPorts: [
    { id: "in_metal", name: "molten_metal", materialClass: "molten_iron", flowMode: "batch" },
    { id: "in_core", name: "sand_core", materialClass: "permanent_mould" },
  ],
  outputPorts: [{ id: "out_castings", name: "castings", materialClass: "raw_casting", flowMode: "discrete_unit" }],
  parameters: [
    { name: "metal_temperature", unit: "°C", value: 720, range: [690, 740], dataKind: "analog_scalar" },
    { name: "die_temperature", unit: "°C", value: 250, range: [180, 300], dataKind: "analog_scalar" },
    { name: "fill_rate", unit: "kg/s", value: 1.5, range: [0.5, 3], dataKind: "analog_scalar" },
    { name: "lpdc_pressure", unit: "mbar", value: 700, range: [500, 1500], dataKind: "analog_scalar" },
    { name: "cycle_time", unit: "s", value: 180, range: [90, 360], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_cast", name: "casting_made", purpose: "pulse", dataKind: "count_scalar" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "CA",
    label: "Casting",
    isPart: true,
    qc: { tests: ["xray", "reduced_pressure_test", "dimensional"], defects: ["oxide_bifilm", "hydrogen_porosity", "misrun"] },
  },
};

// --- Investment casting ------------------------------------------------------

/** Wax injection — inject pattern wax into aluminium tooling. §2/§4 investment. */
const ENT_WAX_INJECTION: EntityDef = {
  id: "ent-wax-injection",
  templateId: "foundry.wax_injection_press",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Injects pattern wax into the die to form the wax patterns.",
  flowMode: "discrete_unit",
  // WS-B#8: rated `cycle_time_s 60 s` deleted — duplicate of the `cycle_time`
  // parameter below, and not a registry key.
  inputPorts: [{ id: "in_wax", name: "pattern_wax", materialClass: "alloy_material" }],
  outputPorts: [{ id: "out_patterns", name: "wax_patterns", materialClass: "pattern" }],
  parameters: [
    { name: "wax_temperature", unit: "°C", value: 65, range: [55, 75], dataKind: "analog_scalar" },
    { name: "injection_pressure", unit: "bar", value: 25, range: [10, 50], dataKind: "analog_scalar" },
    { name: "cycle_time", unit: "s", value: 60, range: [30, 120], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_wax", name: "pattern_made", purpose: "pulse", dataKind: "count_scalar" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "WX",
    label: "Wax pattern",
    isPart: true,
    qc: { tests: ["dimensional", "surface"], defects: ["short_fill", "flash", "surface_defect"] },
  },
};

/** Pattern assembly — gate wax patterns onto a sprue tree. §2 investment. */
const ENT_PATTERN_ASSEMBLY: EntityDef = {
  id: "ent-pattern-assembly",
  templateId: "foundry.pattern_assembly",
  class: "STATION",
  lotOp: "MERGE",
  iconKey: "layers",
  oneLine: "Welds the wax patterns to a central sprue to build a tree.",
  inputPorts: [{ id: "in_patterns", name: "wax_patterns", materialClass: "pattern" }],
  outputPorts: [{ id: "out_tree", name: "wax_tree", materialClass: "pattern" }],
  parameters: [
    { name: "patterns_per_tree", value: 12, range: [4, 40], dataKind: "count_scalar" },
    { name: "tree_weight", unit: "kg", value: 6, range: [1, 20], dataKind: "analog_scalar" },
  ],
};

/** Shell build — 6–8 ceramic dip/stucco coats + dry. §2 investment. */
const ENT_SHELL_BUILD: EntityDef = {
  id: "ent-shell-build",
  templateId: "foundry.shell_build_line",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Builds a ceramic shell around the tree by dipping it in slurry, then in stucco, and drying it, again and again.",
  inputPorts: [{ id: "in_tree", name: "wax_tree", materialClass: "pattern" }],
  outputPorts: [{ id: "out_shell", name: "ceramic_shell", materialClass: "investment_shell" }],
  parameters: [
    { name: "coat_count", value: 7, range: [6, 9], dataKind: "count_scalar" },
    { name: "shell_thickness", unit: "mm", value: 8, range: [6, 12], dataKind: "analog_scalar" },
    { name: "dry_humidity", unit: "%", value: 45, range: [30, 60], dataKind: "analog_percentage" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "SH",
    label: "Shell",
    isPart: false,
    qc: { tests: ["shell_thickness", "visual"], defects: ["shell_crack", "thin_coat"] },
  },
};

/** Dewax + fire — autoclave dewax, then fire shell 900–1100 °C. §2/§4 investment. */
const ENT_DEWAX_FIRE: EntityDef = {
  id: "ent-dewax-fire",
  templateId: "foundry.dewax_fire_furnace",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "Melts the wax out of the shell, then fires the shell in a furnace to burn off what is left and sinter it.",
  flowMode: "batch",
  inputPorts: [{ id: "in_shell", name: "ceramic_shell", materialClass: "investment_shell" }],
  outputPorts: [{ id: "out_shell", name: "fired_shell", materialClass: "investment_shell" }],
  parameters: [
    { name: "fire_temperature", unit: "°C", value: 1000, range: [900, 1100], dataKind: "analog_scalar" },
    { name: "soak_time", unit: "min", value: 60, range: [30, 120], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_fired", name: "shell_ready", purpose: "cycle_end", dataKind: "boolean_state" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "SH",
    label: "Shell",
    isPart: false,
    qc: { tests: ["visual", "preheat_temp"], defects: ["shell_crack", "residual_wax"] },
  },
};

/** Investment pour — fill the hot fired shell with molten metal. §4 pouring. */
const ENT_INVESTMENT_POUR: EntityDef = {
  id: "ent-investment-pour",
  templateId: "foundry.investment_pour_station",
  class: "EQUIPMENT",
  lotOp: "SPLIT",
  iconKey: "cog",
  oneLine: "Pours molten metal into the preheated ceramic shell.",
  inputPorts: [
    { id: "in_metal", name: "molten_metal", materialClass: "molten_iron" },
    { id: "in_shell", name: "fired_shell", materialClass: "investment_shell" },
  ],
  outputPorts: [{ id: "out_poured", name: "poured_shells", materialClass: "poured_mould" }],
  parameters: [
    { name: "pour_temperature", unit: "°C", value: 1580, range: [1550, 1650], dataKind: "analog_scalar" },
    { name: "shell_preheat", unit: "°C", value: 950, range: [850, 1050], dataKind: "analog_scalar" },
    { name: "pour_time", unit: "s", value: 8, range: [3, 15], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_pour", name: "shell_poured", purpose: "pulse", dataKind: "count_scalar" }],
};

/** Shell knockout — vibrate/blast ceramic off the cast tree. §2 investment. */
const ENT_SHELL_KNOCKOUT: EntityDef = {
  id: "ent-shell-knockout",
  templateId: "foundry.shell_knockout",
  class: "EQUIPMENT",
  lotOp: "SPLIT",
  iconKey: "cog",
  oneLine: "Knocks the fired ceramic shell off the casting by vibration or water jet.",
  inputPorts: [{ id: "in_poured", name: "poured_shells", materialClass: "poured_mould" }],
  outputPorts: [
    { id: "out_castings", name: "cast_trees", materialClass: "raw_casting" },
    { id: "out_shell_waste", name: "shell_waste", materialClass: "waste" },
  ],
  parameters: [
    { name: "vibration_frequency", unit: "rpm", value: 1200, range: [900, 1500], dataKind: "analog_scalar" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "CA",
    label: "Casting",
    isPart: true,
    qc: { tests: ["visual"], defects: ["misrun", "shrinkage", "inclusions"] },
  },
};

/** Cut-off — saw / abrasive cut castings off the sprue (investment/continuous). §4 fettling. */
const ENT_CUTOFF: EntityDef = {
  id: "ent-cutoff",
  templateId: "foundry.cutoff_station",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Cuts each casting off the gating with a band saw or an abrasive wheel.",
  flowMode: "discrete_unit",
  inputPorts: [{ id: "in_trees", name: "cast_trees", materialClass: "raw_casting" }],
  outputPorts: [{ id: "out_castings", name: "castings", materialClass: "raw_casting" }],
  parameters: [
    { name: "cut_rate", unit: "parts/h", value: 120, range: [40, 300], dataKind: "analog_scalar" },
    { name: "wheel_wear", unit: "%", value: 30, range: [0, 100], dataKind: "analog_percentage" },
  ],
  boundarySignals: [{ id: "sig_cut", name: "part_cut", purpose: "pulse", dataKind: "count_scalar" }],
};

/** Centrifugal machine — spun mould for symmetric dense castings. §2 centrifugal. */
const ENT_CENTRIFUGAL: EntityDef = {
  id: "ent-centrifugal",
  templateId: "foundry.centrifugal_machine",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "A centrifugal casting machine whose spinning mould throws the metal into a dense wall.",
  flowMode: "discrete_unit",
  inputPorts: [{ id: "in_metal", name: "molten_metal", materialClass: "molten_iron" }],
  outputPorts: [{ id: "out_castings", name: "castings", materialClass: "raw_casting", flowMode: "discrete_unit" }],
  parameters: [
    { name: "spin_speed", unit: "rpm", value: 300, range: [100, 500], dataKind: "analog_scalar" },
    { name: "pour_temperature", unit: "°C", value: 1150, range: [1000, 1250], dataKind: "analog_scalar" },
    { name: "cycle_time", unit: "s", value: 120, range: [60, 240], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_cast", name: "casting_made", purpose: "pulse", dataKind: "count_scalar" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "CA",
    label: "Casting",
    isPart: true,
    qc: { tests: ["xray", "dimensional"], defects: ["segregation", "porosity", "inclusions"] },
  },
};

// --- Finishing / post-processing ---------------------------------------------

/** Shot blast — wheelblast surface clean / residual sand removal. §4 shot blast. */
const ENT_SHOT_BLAST: EntityDef = {
  id: "ent-shot-blast",
  templateId: "foundry.shot_blast",
  class: "EQUIPMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "cog",
  oneLine: "Shot blast wheels clean the scale and leftover sand off the castings.",
  flowMode: "discrete_unit",
  // WS-B#8: rated `throughput 200 kg/h` RETIRED, per dossier 04-finish-ht.md:273
  // ("rated throughput maps to no key — retire or map to a CADENCE key, never a
  // mass key") and :565. It is not re-authored at a corrected value on purpose:
  // the dossier measured the catalog's 200 as 3-6x below a real wheelblast
  // (600-1200 kg/h, :227) and marked it "confirm at Maurya" — so the honest state
  // is no number rather than a second guess. It reached no reader either way.
  inputPorts: [{ id: "in_castings", name: "raw_castings", materialClass: "raw_casting" }],
  outputPorts: [{ id: "out_castings", name: "blasted_castings", materialClass: "raw_casting" }],
  parameters: [
    { name: "wheel_speed", unit: "rpm", value: 2400, range: [1800, 3000], dataKind: "analog_scalar" },
    { name: "shot_flow", unit: "kg/min", value: 180, range: [100, 300], dataKind: "analog_scalar" },
    { name: "exposure_time", unit: "min", value: 6, range: [2, 15], dataKind: "analog_scalar" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "CA",
    label: "Casting",
    isPart: false,
    qc: { tests: ["surface_cleanliness", "residual_sand"], defects: ["under_clean", "impingement"] },
  },
};

/** Fettling / grinding — degate + grind gate stubs + dress. §4 fettling. */
const ENT_FETTLING: EntityDef = {
  id: "ent-fettling",
  templateId: "foundry.fettling_grinding",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Removes the gates and risers and grinds the parting lines smooth.",
  flowMode: "discrete_unit",
  inputPorts: [{ id: "in_castings", name: "blasted_castings", materialClass: "raw_casting" }],
  outputPorts: [{ id: "out_castings", name: "fettled_castings", materialClass: "raw_casting" }],
  parameters: [
    { name: "material_removed", unit: "kg", value: 1.2, range: [0.1, 5], dataKind: "analog_scalar" },
    { name: "grind_time", unit: "min", value: 8, range: [2, 30], dataKind: "analog_scalar" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "CA",
    label: "Casting",
    isPart: true,
    qc: { tests: ["dimensional", "finish"], defects: ["grind_through", "gate_stubs", "surface_cracks"] },
  },
};

/** Heat-treat furnace — anneal / normalise / Q&T / solution+age. §4 heat treatment. */
const ENT_HEAT_TREAT: EntityDef = {
  id: "ent-heat-treat",
  templateId: "foundry.heat_treat_furnace",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "A heat treatment furnace for annealing, normalising, quenching and tempering, or solution treatment and ageing.",
  flowMode: "batch",
  ratedProperties: [
    { name: "ht_batch_capacity_kg", value: 3000, unit: "kg" },
    // WS-B#8: `max_temperature` renamed to the registry key the dossier
    // prescribes (04-finish-ht.md:300 + :562 "add ht_max_temp_c"; catalog 1100,
    // real band 1100-1350). Wires-to is an ALARM, not the heat emitter — it is
    // classified NAMEPLATE_CONTROL in the connectivity manifest, because a
    // customer will assume a declared max temperature is enforced. It is not.
    { name: "ht_max_temp_c", value: 1100, unit: "°C" },
  ],
  inputPorts: [{ id: "in_castings", name: "castings", materialClass: "raw_casting", flowMode: "batch" }],
  outputPorts: [{ id: "out_castings", name: "treated_castings", materialClass: "raw_casting", flowMode: "batch" }],
  parameters: [
    { name: "furnace_temperature", unit: "°C", value: 900, range: [150, 1100], dataKind: "analog_scalar" },
    { name: "soak_time", unit: "h", value: 4, range: [1, 12], dataKind: "analog_scalar" },
    { name: "ramp_rate", unit: "°C/h", value: 120, range: [50, 300], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_ht_done", name: "cycle_complete", purpose: "cycle_end", dataKind: "boolean_state" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "CA",
    label: "Casting",
    isPart: true,
    qc: { tests: ["hardness", "tensile", "microstructure"], defects: ["soft_spots", "distortion", "decarb", "quench_cracks"] },
  },
};

/** CNC machining — finish-machine cast features to drawing. §4 machining (Q13). */
const ENT_MACHINING: EntityDef = {
  id: "ent-machining",
  templateId: "foundry.cnc_machining_cell",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "A CNC machining cell that finish machines the datums and working features.",
  flowMode: "discrete_unit",
  // WS-B#8: rated `cycle_time_s 300 s` deleted — duplicate of the `cycle_time`
  // parameter below. Dossier 04-finish-ht.md:565 names this exact row.
  //
  // WS-D roleMachining (2026-08-03) — THE WRITER. `machining_removal_pct` had live
  // readers only after the Go role landed, and no template anywhere authored it, so the
  // wire was unfed: the panel offered "Stock removal" and no plant ever shipped a value.
  // The emitter reads it at `emitMachiningSwarf` and debits an edged `machining_swarf`
  // lot; UNRATED means the swarf mass is WITHHELD with its reason (never a "typical"
  // 5-15 % invented at emit time), so authoring it here is what makes the debit reachable
  // from a customer's seat. Value = the midpoint of the dossier's grey/ductile band
  // (04-finish-ht.md:389 — "5-15 [2-30] % of casting"); it is a DEFAULT the customer
  // edits in the config panel, and the heat trace discloses it as ESTIMATED with the
  // basis naming this rating, never as a weighed bin.
  ratedProperties: [{ name: "machining_removal_pct", value: 10, unit: "%" }],
  inputPorts: [{ id: "in_castings", name: "castings", materialClass: "raw_casting" }],
  outputPorts: [{ id: "out_castings", name: "machined_castings", materialClass: "raw_casting" }],
  parameters: [
    { name: "cycle_time", unit: "s", value: 300, range: [60, 900], dataKind: "analog_scalar" },
    { name: "spindle_load", unit: "%", value: 55, range: [20, 90], dataKind: "analog_percentage" },
    { name: "tool_wear", unit: "%", value: 40, range: [0, 100], dataKind: "analog_percentage" },
  ],
  boundarySignals: [{ id: "sig_machined", name: "part_machined", purpose: "pulse", dataKind: "count_scalar" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "CA",
    label: "Casting",
    isPart: true,
    qc: { tests: ["cmm", "dimensional", "surface"], defects: ["out_of_tolerance", "tool_marks", "exposed_porosity"] },
  },
};

/** NDT / metrology lab — RT/PT/UT/CMM/leak final inspection. §4 final test lab. */
const ENT_NDT: EntityDef = {
  id: "ent-ndt",
  templateId: "foundry.ndt_metrology_lab",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine: "A lab that inspects castings by non-destructive testing (NDT), with X-ray, dye penetrant and leak tests, and measures them on a coordinate measuring machine.",
  inputPorts: [{ id: "in_castings", name: "castings", materialClass: "raw_casting" }],
  outputPorts: [
    { id: "out_accepted", name: "accepted_castings", materialClass: "finished_casting" },
    { id: "out_rejected", name: "rejected_castings", materialClass: "raw_casting" },
  ],
  parameters: [
    { name: "defect_count", value: 3, dataKind: "count_scalar" },
    { name: "rejection_rate", unit: "%", value: 2.5, dataKind: "analog_percentage" },
    { name: "leak_rate", unit: "cc/min", value: 0.4, range: [0, 5], dataKind: "analog_scalar" },
  ],
};

// ── Standard Green-Sand Foundry (STD archetype) ──────────────────────────────
// Authored equipment for the "Standard Green-Sand Foundry" plant type. Every
// def carries category:"STD" so the add-entity catalog badges it as part of the
// curated standard set. Role-signature names (Treatment Ladle, Core Shooter,
// Sand Laboratory, …) + material classes drive the genealogy role classifier;
// seed `value`s + ratedProperties are chosen so a freshly-built plant runs a
// mass-balanced ~90-casting heat with linked cores + closed sand/return loops.
// See the approved plan (let-s-take-a-moment-polished-lamport.md) §B and the
// Seed-values section.

// --- Melt line (treatment) ---------------------------------------------------

/** Mg / treatment ladle (ductile) — FeSiMg nodularises the melt; fade-limited.
 *  Grey iron uses this as a plain holding ladle (the archetype omits the Mg
 *  params via override). STD. */
const ENT_STD_TREATMENT_LADLE: EntityDef = {
  id: "ent-std-treatment-ladle",
  templateId: "foundry.std_treatment_ladle",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "A treatment ladle where a ferrosilicon magnesium alloy makes the graphite of ductile iron form nodules. The effect fades from the moment of treatment.",
  category: "STD",
  flowMode: "batch",
  ratedProperties: [
    { name: "capacity_kg", value: 1500, unit: "kg" },
    { name: "mg_window_sec", value: 600, unit: "s" },
    { name: "base_iron_max_s_pct", value: 0.020, unit: "%" },
    // WS-D INV-M1 — the treatment standing rate, as ALLOY mass %. Note it is NOT
    // the `mg_addition_pct` parameter below: that port is the MAGNESIUM element
    // percent (0.15 %), and 1.4 % of a 5.5 %-Mg alloy is 0.077 % Mg. One key, one
    // quantity, one unit; the port keeps its own and is answered in it.
    { name: "nodulariser_addition_pct", value: 1.4, unit: "%" },
  ],
  inputPorts: [
    { id: "in_metal", name: "base_metal", materialClass: "molten_iron", flowMode: "batch" },
    { id: "in_fesimg", name: "fesimg_alloy", materialClass: "alloy_material" },
  ],
  outputPorts: [{ id: "out_metal", name: "treated_metal", materialClass: "molten_iron", flowMode: "batch" }],
  parameters: [
    { name: "mg_addition_pct", unit: "%", value: 0.15, range: [0.05, 0.3], dataKind: "analog_percentage" },
    { name: "mg_recovery_pct", unit: "%", value: 50, range: [30, 70], dataKind: "analog_percentage" },
    { name: "treatment_temp", unit: "°C", value: 1480, range: [1450, 1510], dataKind: "analog_scalar" },
    { name: "fade_time", unit: "s", value: 300, range: [60, 600], dataKind: "analog_scalar" },
    // The per-ladle measured outcome of the treatment: port + range, no invented nominal
    // (same disposition as foundry.mg_treatment_station's residual_mg, WS-D / MET §3).
    { name: "residual_mg", unit: "%", range: [0.035, 0.05], dataKind: "analog_percentage", paramClass: "chemistry" },
  ],
  boundarySignals: [{ id: "sig_treat_done", name: "treatment_complete", purpose: "cycle_end", dataKind: "boolean_state" }],
};

/** Inoculation & auto-pour ladle — late FeSi inoculation just before pour. STD. */
const ENT_STD_INOCULATION_LADLE: EntityDef = {
  id: "ent-std-inoculation-ladle",
  templateId: "foundry.std_inoculation_ladle",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "A ladle that adds a ferrosilicon inoculant just before pouring, which promotes graphite and prevents chill.",
  category: "STD",
  flowMode: "batch",
  ratedProperties: [
    { name: "capacity_kg", value: 1500, unit: "kg" },
    // WS-D INV-M1 — the ladle's standing inoculant rate. Until this shipped, the
    // `inoculant_addition` port below was DRAWN on every heat (0.11-0.435 % on
    // stage-1-test-sim, each with an inSpec flag) beside an inoculant port that had
    // never carried a kilogram. The rating now sizes a real FeSi lot and the port
    // reports it.
    { name: "inoculant_addition_pct", value: 0.2, unit: "%" },
  ],
  inputPorts: [
    { id: "in_metal", name: "treated_metal", materialClass: "molten_iron", flowMode: "batch" },
    { id: "in_inoculant", name: "inoculant", materialClass: "alloy_material" },
  ],
  outputPorts: [{ id: "out_metal", name: "inoculated_metal", materialClass: "molten_iron", flowMode: "batch" }],
  parameters: [
    { name: "inoculant_addition", unit: "%", value: 0.2, range: [0.1, 0.6], dataKind: "analog_percentage" },
    { name: "pour_temperature", unit: "°C", value: 1380, range: [1340, 1450], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_inoc_done", name: "inoculation_complete", purpose: "cycle_end", dataKind: "boolean_state" }],
};

// --- Melt-line: charge yard (bought-material boundary) ----------------------

/** Charge yard — purchase boundary issuing pig iron, steel scrap and alloy
 *  additions into charge prep (#677). A source/storage stage like
 *  ENT_STD_RAW_MATERIAL_STORE below: no input ports, so its outputs are
 *  grounded as bought/consumed material — never confusable with the plant's
 *  own internal-return loop (degating runners/risers, inspection rejects),
 *  which lands on charge prep's separate in_returns/in_rejects ports. STD. */
const ENT_STD_CHARGE_YARD: EntityDef = {
  id: "ent-std-charge-yard",
  templateId: "foundry.std_charge_yard",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "The purchase point for the pig iron, steel scrap and alloy additions of the charge.",
  category: "STD",
  inputPorts: [],
  outputPorts: [
    { id: "out_pig", name: "pig_iron", materialClass: "pig_iron" },
    { id: "out_scrap", name: "steel_scrap", materialClass: "steel_scrap" },
    { id: "out_alloy", name: "alloy_additions", materialClass: "alloy_material" },
  ],
  parameters: [
    { name: "pig_iron_stock", unit: "t", value: 40, range: [0, 120], dataKind: "analog_scalar" },
    { name: "steel_scrap_stock", unit: "t", value: 25, range: [0, 100], dataKind: "analog_scalar" },
  ],
};

/** Pattern shop / tool room — issues the reusable moulding pattern to the
 *  moulding machine (#698). ENT_MOULDING (foundry-plant.ts, shared verbatim by
 *  every sand/no-bake archetype) already declares `in_pattern: pattern` —
 *  it was simply never wired in the STD archetype, so the muller's green
 *  sand was the moulding machine's ONLY in-edge. A source/storage stage like
 *  ENT_STD_CHARGE_YARD above: no input ports, so its output is grounded
 *  (pattern's disposition is `intermediate`, not `circulating`/`return`). STD. */
const ENT_STD_PATTERN_SHOP: EntityDef = {
  id: "ent-std-pattern-shop",
  templateId: "foundry.std_pattern_shop",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Keeps the reusable moulding patterns and issues them to the moulding machine.",
  category: "STD",
  inputPorts: [],
  outputPorts: [{ id: "out_pattern", name: "pattern", materialClass: "pattern" }],
  parameters: [],
};

// --- Sand plant: stores / hoppers / bunkers ---------------------------------

/** Raw material store — bulk silos for silica, bentonite, sea-coal, water. STD. */
const ENT_STD_RAW_MATERIAL_STORE: EntityDef = {
  id: "ent-std-raw-material-store",
  templateId: "foundry.std_raw_material_store",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Bulk silos that hold the silica, bentonite, sea coal and water of the sand system.",
  category: "STD",
  inputPorts: [
    { id: "in_silica", name: "silica_sand", materialClass: "silica_sand" },
    { id: "in_bentonite", name: "bentonite", materialClass: "bentonite" },
    { id: "in_sea_coal", name: "sea_coal", materialClass: "sea_coal" },
    { id: "in_water", name: "water", materialClass: "water" },
  ],
  outputPorts: [
    { id: "out_silica", name: "silica_sand", materialClass: "silica_sand" },
    { id: "out_bentonite", name: "bentonite", materialClass: "bentonite" },
    { id: "out_sea_coal", name: "sea_coal", materialClass: "sea_coal" },
    { id: "out_water", name: "water", materialClass: "water" },
  ],
  parameters: [
    { name: "silica_stock", unit: "t", value: 120, range: [0, 300], dataKind: "analog_scalar" },
    { name: "bentonite_stock", unit: "t", value: 18, range: [0, 60], dataKind: "analog_scalar" },
    { name: "sea_coal_stock", unit: "t", value: 9, range: [0, 30], dataKind: "analog_scalar" },
  ],
};

/** Batch hopper — weigh-doses new sand additives, metered straight through to the
 *  muller's matching fresh-material ports (#698). Each material keeps its own bought
 *  identity end to end: the hopper doses by weight but does not physically pre-blend
 *  silica/bentonite/sea-coal/water into one stream (that mixing is the muller's job,
 *  ENT_SAND_MIXER's TRANSFORM) — so PASSTHROUGH, one port pair per material, not a
 *  MERGE into a single "premix" port. Previously this had ONE merged output port
 *  wrongly classed `green_sand` (the recirculating post-mix product's class, not a
 *  fresh-material class) wired onto the muller's `in_silica` port — a PE-1 class
 *  mismatch that also meant the muller (and everything downstream: moulding, the
 *  sand-lab instruments, the sieve/separator/cooler/return-silo reclaim loop) never
 *  saw a single purchased-material in-edge anywhere in the sand spine (MC-2). STD. */
const ENT_STD_BATCH_HOPPER: EntityDef = {
  id: "ent-std-batch-hopper",
  templateId: "foundry.std_batch_hopper",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Weighs out the silica, bentonite, sea coal and water for each batch and feeds them to the muller.",
  category: "STD",
  inputPorts: [
    { id: "in_silica", name: "silica_sand", materialClass: "silica_sand" },
    { id: "in_bentonite", name: "bentonite", materialClass: "bentonite" },
    { id: "in_sea_coal", name: "sea_coal", materialClass: "sea_coal" },
    { id: "in_water", name: "water", materialClass: "water" },
  ],
  outputPorts: [
    { id: "out_silica", name: "silica_sand", materialClass: "silica_sand" },
    { id: "out_bentonite", name: "bentonite", materialClass: "bentonite" },
    { id: "out_sea_coal", name: "sea_coal", materialClass: "sea_coal" },
    { id: "out_water", name: "water", materialClass: "water" },
  ],
  parameters: [
    { name: "silica_dose", unit: "kg", value: 2.4, range: [0, 10], dataKind: "analog_scalar" },
    { name: "bentonite_dose", unit: "kg", value: 3.2, range: [0, 10], dataKind: "analog_scalar" },
    { name: "sea_coal_pct", unit: "%", value: 4, range: [2, 8], dataKind: "analog_percentage" },
    { name: "water_dose", unit: "L", value: 12, range: [0, 40], dataKind: "analog_scalar" },
  ],
};

/** Prepared sand bunker — surge buffer of conditioned green sand. `in_trim_water`
 *  (#698) is a small moisture-trim addition at the buffer outlet — sand held in
 *  surge storage loses moisture to evaporation, and this is the fast lever the
 *  compactability controller's own water-dose setpoint (ENT_STD_COMPACTABILITY_
 *  CONTROLLER) already exists to correct for; distributing a trim dose across the
 *  buffer chain rather than concentrating all make-up at the muller is standard
 *  green-sand practice. STD. */
const ENT_STD_PREPARED_BUNKER: EntityDef = {
  id: "ent-std-prepared-bunker",
  templateId: "foundry.std_prepared_sand_bunker",
  class: "TRANSPORT",
  lotOp: "PASSTHROUGH",
  iconKey: "truck",
  oneLine: "A bunker of prepared green sand that buffers the supply to moulding.",
  category: "STD",
  inputPorts: [
    { id: "in_sand", name: "green_sand", materialClass: "green_sand" },
    { id: "in_trim_water", name: "trim_water", materialClass: "water" },
  ],
  outputPorts: [{ id: "out_sand", name: "green_sand", materialClass: "green_sand" }],
  parameters: [{ name: "level", unit: "%", value: 70, range: [0, 100], dataKind: "analog_percentage" }],
};

/** Moulding-machine hopper — final sand buffer feeding the moulding machine.
 *  `in_trim_water` (#698) — same reasoning as ENT_STD_PREPARED_BUNKER above,
 *  applied at the LAST checkpoint before the moulding machine, where operators
 *  want final compactability correction. STD. */
const ENT_STD_MOULDING_HOPPER: EntityDef = {
  id: "ent-std-moulding-hopper",
  templateId: "foundry.std_moulding_machine_hopper",
  class: "TRANSPORT",
  lotOp: "PASSTHROUGH",
  iconKey: "truck",
  oneLine: "The last buffer of green sand, at the moulding machine.",
  category: "STD",
  inputPorts: [
    { id: "in_sand", name: "green_sand", materialClass: "green_sand" },
    { id: "in_trim_water", name: "trim_water", materialClass: "water" },
  ],
  outputPorts: [{ id: "out_sand", name: "green_sand", materialClass: "green_sand" }],
  parameters: [{ name: "level", unit: "%", value: 65, range: [0, 100], dataKind: "analog_percentage" }],
};

// --- Sand reclamation loop ---------------------------------------------------
//
// Every stage below except std_dust_extractor now also grounds itself with a
// small `in_makeup` (silica_sand) or evaporative `in_evap_water` (water) input
// from std_raw_material_store (#698) — see the wiring comment on that stage in
// archetypes.ts's stdCommonStages() for the full reasoning. The reclaim line
// already tracks two real loss points as `waste` (magnetic_separator's
// out_tramp, this loop's own out_dust further down) — a matching small
// fresh-silica top-up at each processing step keeps total system sand mass
// from silently draining as those losses accumulate, rather than concentrating
// 100% of make-up at the muller's own inlet.

/** Polygon sieving / lump crusher — breaks return-sand lumps + screens. STD. */
const ENT_STD_POLYGON_SIEVE: EntityDef = {
  id: "ent-std-polygon-sieve",
  templateId: "foundry.std_polygon_sieve",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Breaks up lumps and sieves the return sand from shakeout.",
  category: "STD",
  flowMode: "continuous",
  // WS-B#9b: dossier 02-sand-core.md:449 ("Sieve throughput | t/h | 30 [10-60]").
  //
  // The `throughput` PARAMETER below carries the same 30 t/h, and that is deliberate
  // here rather than the WS-B#8 duplication: a rating and a live tag answer two
  // different questions (what the machine is RATED for vs what it is DOING), and
  // this is exactly the shape foundry.nobake_continuous_mixer already ships (rated
  // flow_rate_t_per_h 20 alongside the streamed `mix_rate` 20 t/h). Sieve throughput
  // is genuinely instrumentable, so the port has a real occupant — unlike
  // `bench_life`, which is a nameplate wearing a data port's clothes.
  ratedProperties: [{ name: "flow_rate_t_per_h", value: 30, unit: "t/h" }],
  inputPorts: [
    { id: "in_sand", name: "return_sand", materialClass: "return_sand" },
    { id: "in_makeup", name: "makeup_sand", materialClass: "silica_sand" },
  ],
  outputPorts: [{ id: "out_sand", name: "return_sand", materialClass: "return_sand" }],
  parameters: [
    { name: "throughput", unit: "t/h", value: 30, range: [10, 60], dataKind: "analog_scalar" },
    { name: "screen_size", unit: "mm", value: 6, range: [2, 16], dataKind: "analog_scalar" },
  ],
};

/** Magnetic separator — pulls tramp iron / shot from the return sand. STD. */
const ENT_STD_MAGNETIC_SEPARATOR: EntityDef = {
  id: "ent-std-magnetic-separator",
  templateId: "foundry.std_magnetic_separator",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "A magnet that removes tramp iron and spilled shot from the return sand.",
  category: "STD",
  flowMode: "continuous",
  inputPorts: [
    { id: "in_sand", name: "return_sand", materialClass: "return_sand" },
    { id: "in_makeup", name: "makeup_sand", materialClass: "silica_sand" },
  ],
  outputPorts: [
    { id: "out_sand", name: "return_sand", materialClass: "return_sand" },
    { id: "out_tramp", name: "tramp_metal", materialClass: "waste" },
  ],
  parameters: [
    { name: "field_strength", unit: "mT", value: 350, range: [100, 800], dataKind: "analog_scalar" },
    { name: "tramp_removed", unit: "%", value: 1.5, range: [0, 5], dataKind: "analog_percentage" },
  ],
};

/** Sand cooler — fluidised-bed / evaporative cool of return sand to the muller.
 *  `in_evap_water` (#698) is exactly what "evaporative cool" means — a
 *  fluidised-bed sand cooler injects a metered water mist that flashes off,
 *  carrying heat with it. STD. */
const ENT_STD_SAND_COOLER: EntityDef = {
  id: "ent-std-sand-cooler",
  templateId: "foundry.std_sand_cooler",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "A cooler that brings the return sand back toward room temperature in a fluidised bed, with evaporating water.",
  category: "STD",
  flowMode: "continuous",
  ratedProperties: [
    { name: "flow_rate_t_per_h", value: 30, unit: "t/h" },
    { name: "rated_power_kw", value: 30, unit: "kW" },
  ],
  inputPorts: [
    { id: "in_sand", name: "return_sand", materialClass: "return_sand" },
    { id: "in_evap_water", name: "evaporative_water", materialClass: "water" },
  ],
  outputPorts: [{ id: "out_sand", name: "return_sand", materialClass: "return_sand" }],
  parameters: [
    // The cooler's own probes: the return sand as it enters the cooler and as it leaves it, in the discharge chute before
    // the silo and the muller. Not the sand laboratory's sand_temperature, which is a sample as it is drawn at a lab
    // sample point (2026-10-01).
    { name: "sand_temp_in", unit: "°C", value: 90, range: [50, 160], dataKind: "analog_scalar", paramClass: "temperature" },
    { name: "sand_temp_out", unit: "°C", value: 38, range: [25, 55], dataKind: "analog_scalar", paramClass: "temperature" },
    { name: "moisture", unit: "%", value: 2.2, range: [0.5, 4], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "moisture_in", unit: "%", value: 1.2, range: [0, 4], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "motor_load", unit: "%", value: 60, range: [0, 100], dataKind: "analog_percentage", paramClass: "other" },
    { name: "water_added", unit: "L/min", value: 15, range: [0, 80], dataKind: "analog_scalar", paramClass: "rate" },
  ],
};

/** Return sand silo — buffers cooled reclaimed sand ahead of the muller. STD. */
const ENT_STD_RETURN_SILO: EntityDef = {
  id: "ent-std-return-silo",
  templateId: "foundry.std_return_sand_silo",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Holds the cooled return sand before it is prepared again.",
  category: "STD",
  // WS-B#9c (2026-08-03). Dossier 02-sand-core.md:507 "Silo capacity (each) | t |
  // … return 200" → 200 000 kg. It is `storage_capacity_kg`, NOT `capacity_kg`:
  // the Go emitter reads capacity_kg on a TRACKED bulk producer as that lot's
  // per-heat BATCH mass (emitTrackedBulkLot), so 200 000 under that name would be
  // a 200-tonne sand lot on one heat. A silo holds a STOCK; a muller makes a
  // BATCH. NAMEPLATE — no reader, and the panel says so.
  ratedProperties: [{ name: "storage_capacity_kg", value: 200000, unit: "kg" }],
  inputPorts: [
    { id: "in_sand", name: "return_sand", materialClass: "return_sand" },
    { id: "in_makeup", name: "makeup_sand", materialClass: "silica_sand" },
  ],
  outputPorts: [{ id: "out_sand", name: "return_sand", materialClass: "return_sand" }],
  parameters: [{ name: "level", unit: "%", value: 60, range: [0, 100], dataKind: "analog_percentage" }],
};

/** Dust extractor — bag-house draws fugitive fines/dust off the AIR around the
 *  sand-plant stations, not off a bulk sand stream (#698 — see the wiring
 *  comment on std_dust_extractor in archetypes.ts's stdCommonStages() for why
 *  it deliberately carries no material in-edge). `in_sand`/`out_sand` are kept
 *  declared-but-unwired, same as std_raw_material_store's own unused input
 *  ports: schema symmetry with the sibling reclaim-loop equipment, not a live
 *  connection. STD. */
const ENT_STD_DUST_EXTRACTOR: EntityDef = {
  id: "ent-std-dust-extractor",
  templateId: "foundry.std_dust_extractor",
  class: "EQUIPMENT",
  lotOp: "SPLIT",
  iconKey: "cog",
  oneLine: "A bag house that draws the fines and dust off the sand plant stations.",
  category: "STD",
  flowMode: "continuous",
  inputPorts: [{ id: "in_sand", name: "return_sand", materialClass: "return_sand" }],
  outputPorts: [
    { id: "out_sand", name: "return_sand", materialClass: "return_sand" },
    { id: "out_dust", name: "dust", materialClass: "waste" },
  ],
  parameters: [
    { name: "airflow", unit: "Nm³/h", value: 24000, range: [5000, 60000], dataKind: "analog_scalar" },
    { name: "dp", unit: "mbar", value: 15, range: [5, 40], dataKind: "analog_scalar" },
  ],
};

/** Dust storage — sealed silo collecting bag-house dust for disposal. STD. */
const ENT_STD_DUST_STORAGE: EntityDef = {
  id: "ent-std-dust-storage",
  templateId: "foundry.std_dust_storage",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "A sealed silo that collects the bag house dust for disposal.",
  category: "STD",
  inputPorts: [{ id: "in_dust", name: "dust", materialClass: "waste" }],
  outputPorts: [],
  parameters: [{ name: "level", unit: "%", value: 40, range: [0, 100], dataKind: "analog_percentage" }],
};

/** Compactability controller — samples muller sand, writes a water-dose
 *  setpoint. A carve-out for testing, same reasoning as ENT_STD_SAND_LAB
 *  above (#698). STD. */
const ENT_STD_COMPACTABILITY_CONTROLLER: EntityDef = {
  id: "ent-std-compactability-controller",
  templateId: "foundry.std_compactability_controller",
  class: "INSTRUMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine: "An inline sand probe that trims the muller's water dose to hold compactability.",
  category: "STD",
  inputPorts: [{ id: "in_sample", name: "green_sand_sample", materialClass: "test_specimen" }],
  outputPorts: [{ id: "out_sample", name: "analyzed_sample", materialClass: "test_specimen" }],
  parameters: [
    { name: "compactibility", unit: "%", value: 42, range: [35, 50], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "water_dose_setpoint", unit: "L", value: 12, range: [0, 40], dataKind: "analog_scalar", valueSource: "manual", paramClass: "setpoint", setpointOf: "water_added" },
    // What the controller measures and does each batch. The target it steers to is a
    // setpoint paired with compactibility, so setpoint tracking needs no name guessing.
    { name: "compactibility_setpoint", unit: "%", value: 42, range: [30, 60], dataKind: "analog_percentage", valueSource: "manual", paramClass: "setpoint", setpointOf: "compactibility" },
    { name: "water_added", unit: "L", value: 12, range: [0, 40], dataKind: "analog_scalar", paramClass: "rate" },
    { name: "green_compression_strength", unit: "g/cm²", value: 1500, range: [800, 2000], dataKind: "analog_scalar", paramClass: "sand_property" },
    // Probe conductivity is a moisture proxy; its unit is instrument-specific.
    { name: "conductivity", value: 0.5, range: [0, 2], dataKind: "analog_scalar", paramClass: "sand_property" },
    // The two bonds are the controller vendor's formulas over green strength, compactability and moisture, so they are
    // derived_kpi, not measurements (2026-10-01). Each is computed from ONE sample, never from window averages, and a
    // controller's value is not pooled with the lab's of the same name without a bias check: two vendors' formulas differ.
    { name: "working_bond", unit: "%", value: 4, range: [2, 7], dataKind: "derived_kpi", paramClass: "sand_property" },
    { name: "available_bond", unit: "%", value: 6, range: [3, 10], dataKind: "derived_kpi", paramClass: "sand_property" },
    // The bond (bentonite) the controller calls for each batch and what was dosed, the clay pair beside the water
    // pair above (2026-10-01): a controller that tracks available bond trims the bond addition to hold it. A batch
    // takes up to 40 kg of bond.
    { name: "bond_addition_setpoint", unit: "kg", value: 3, range: [0, 40], dataKind: "analog_scalar", valueSource: "manual", paramClass: "setpoint", setpointOf: "bond_added" },
    { name: "bond_added", unit: "kg", value: 3, range: [0, 40], dataKind: "analog_scalar", paramClass: "rate" },
  ],
};

// --- Sand lab (multi-input QC) + VCAT ----------------------------------------

/** The share of the sand sieved that is retained on each standard AFS sieve, as a lab's sieve analysis reports it (AFS
 *  1105; the grain fineness number is AFS 1106): the sand lab's sieve series. One naming pattern for every sieve,
 *  `sieve_retained_<mesh>_pct`, with the mesh as the ASTM number of the US series the pack holds
 *  (packs/foundry/sieve-sets/afs_us_series.yaml) and `pan` for what passes the last sieve, so two plants' sheets land on
 *  the same names. The unit is percent of the mass sieved, the AFS convention and the one the grain fineness number is
 *  worked from (a share needs no sample mass, so two sample sizes compare); the platform stores every mass in kg, so a
 *  gram column would not keep its unit. For a used green sand the mass sieved is the sand left after the AFS clay is
 *  washed out, so the twelve shares add to 100 % (99 to 101). A dry sieve of unwashed used sand puts the clay in the
 *  pan and is not these quantities, and a new sand's sieve is not pooled with a system sand's. The masses in grams are
 *  the sieve shaker's (std_sieve_shaker), where they are keyed. The twelve shares are one closed composition: do not
 *  correlate or regress them raw, use the grain fineness, the fines and the spread over the three main sieves. The
 *  values are one analysis of a medium silica system sand: they add to 100 % and give the grain fineness number the
 *  lab's own parameter holds (55). The bands cover a silica sand of grain fineness 45 to 70, are indicative, and a
 *  plant sets its own. */
const SAND_LAB_SIEVE_SERIES: { mesh: string; value: number; range: [number, number] }[] = [
  { mesh: "6", value: 0, range: [0, 0.5] },
  { mesh: "12", value: 0, range: [0, 1] },
  { mesh: "20", value: 0.1, range: [0, 2] },
  { mesh: "30", value: 1, range: [0, 6] },
  { mesh: "40", value: 10, range: [2, 22] },
  { mesh: "50", value: 32, range: [10, 40] },
  { mesh: "70", value: 31, range: [20, 40] },
  { mesh: "100", value: 15, range: [8, 30] },
  { mesh: "140", value: 7.9, range: [3, 16] },
  { mesh: "200", value: 2, range: [0.5, 7] },
  { mesh: "270", value: 0.7, range: [0, 3] },
  { mesh: "pan", value: 0.3, range: [0, 2] },
];

/** Sand laboratory — multi-input bench QC sampling the green-sand system at four
 *  points (muller, prepared bunker, return silo, moulding). Non-consuming.
 *  Each inlet is a carve-out DIVERTED from its source stream for destructive
 *  bench testing — economically an OUTPUT leaving the mass flow, not more of
 *  the circulating green_sand/return_sand it was drawn from (classes.yaml's
 *  own test_specimen notes: "Separately-cast test bars OR SAND SAMPLES drawn
 *  for lab testing"). Mirrors ENT_QC's out_accepted(finished_casting) →
 *  tensile's in_bars(test_specimen) wire: the SOURCE port keeps its real
 *  class, only the sample wire + this inlet are test_specimen (#698). STD. */
const ENT_STD_SAND_LAB: EntityDef = {
  id: "ent-std-sand-lab",
  templateId: "foundry.std_sand_laboratory",
  class: "INSTRUMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine: "The sand lab that tests the green sand system, sampled at four points.",
  category: "STD",
  inputPorts: [
    { id: "in_muller", name: "muller_sample", materialClass: "test_specimen" },
    { id: "in_bunker", name: "prepared_sample", materialClass: "test_specimen" },
    { id: "in_return", name: "return_sample", materialClass: "test_specimen" },
    { id: "in_moulding", name: "moulding_sample", materialClass: "test_specimen" },
  ],
  outputPorts: [{ id: "out_sample", name: "analyzed_sample", materialClass: "test_specimen" }],
  parameters: [
    { name: "green_compression_strength", unit: "g/cm²", value: 1500, range: [800, 2000], dataKind: "analog_scalar", paramClass: "sand_property" },
    { name: "compactibility", unit: "%", value: 42, range: [35, 50], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "moisture_content", unit: "%", value: 3.8, range: [3.0, 4.2], dataKind: "analog_percentage", paramClass: "sand_property" },
    // The grain fineness number the lab reports. A plant that sieves on an AFS sieve shaker records the mass on
    // each screen there (std_sieve_shaker: manual mesh tags and the pack's GFN computed tag, provision-sieve.ts),
    // not here, so no screen is entered twice and no GFN is computed from a second copy of the same masses. What
    // this lab holds of the sieve test is what its own report gives, read by import: the GFN here, and the share
    // retained on each screen (sieve_retained_<mesh>_pct, below).
    { name: "afs_grain_fineness", unit: "AFS", value: 55, range: [45, 70], dataKind: "analog_scalar", paramClass: "sand_property" },
    // Methylene-blue clay (AFS 2210): only the LIVE bentonite, the clay still able to bond, by dye adsorption. Not
    // the washed AFS clay below. The dye test is calibrated on a reference bentonite and reads in terms of it, so the
    // lab's reference bentonite is to be recorded with its results (no field carries it yet), and a change of
    // reference is a step in the series. A reading above the washed AFS clay is an entry error, not a finding. A
    // plant's washed AFS clay belongs in afs_clay_content below, never in this field.
    { name: "active_clay_content", unit: "%", value: 9, range: [5, 13], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "permeability_number", unit: "AFS", value: 120, range: [80, 180], dataKind: "analog_scalar", paramClass: "sand_property" },
    // Loss on ignition: the combustibles (sea coal, binder residue) in the system sand.
    { name: "loi", unit: "%", value: 3.5, range: [1, 7], dataKind: "analog_percentage", paramClass: "sand_property" },
    // The rest of a commercial green-sand lab's report (2026-10-01). Every green-sand strength is in g/cm², the
    // unit green_compression_strength uses (an import in psi is scaled to it by its extractor, as that one is).
    //
    // Washed AFS clay (AFS 2110): everything that settles slower than an inch a minute in water, about 20 µm and
    // finer: live clay AND dead (burnt) clay, coal dust and silica fines. A different quantity from the
    // methylene-blue clay above, and always higher; the gap is the dead clay and fines.
    { name: "afs_clay_content", unit: "%", value: 11, range: [7, 15], dataKind: "analog_percentage", paramClass: "sand_property" },
    // Fines: the washed sand finer than the 140 sieve (106 µm), what is on the 200 and 270 sieves and in the pan, as a
    // share of the WHOLE sample, clay included. A share of the washed sand reads about 12 % higher at 11 % clay, and a
    // lab may cut at the 200 sieve instead, so a reading's sieve cut and basis are to be stated with it (no field
    // carries them yet).
    { name: "fines", unit: "%", value: 3, range: [1, 6], dataKind: "analog_percentage", paramClass: "sand_property" },
    // The share retained on each AFS sieve, #6 to the pan (AFS 1105): the sieve analysis behind the grain fineness number,
    // one name per sieve, sieve_retained_<mesh>_pct (SAND_LAB_SIEVE_SERIES says why percent, and of what). A plant's own
    // fines column is `fines`, never a sum of these: the fines above are on the whole-sample basis and these shares are of
    // the washed sand. A plant that records grams on a sieved mass of known, constant weight (after washing) imports them
    // as percent, scaled by its extractor; grams of any other mass are not these quantities.
    ...SAND_LAB_SIEVE_SERIES.map(
      ({ mesh, value, range }): ParameterDef => ({
        name: `sieve_retained_${mesh}_pct`,
        unit: "%",
        value,
        range,
        dataKind: "analog_percentage",
        paramClass: "sand_property",
      }),
    ),
    // Return sand sampled AFTER the cooler, at the muller inlet: the water it brings in, which the muller's dose must
    // make up. The sample point is part of the quantity: sand taken before the cooler differs in moisture and
    // temperature, because the cooler sprays and evaporates water; the direction is the plant's. Readings from the
    // two points are never pooled.
    { name: "return_sand_moisture", unit: "%", value: 1.5, range: [0.8, 3], dataKind: "analog_percentage", paramClass: "sand_property" },
    // The clay at work and the clay available, as a muller controller reports them (std_compactability_controller):
    // working bond from green strength and compactability, available bond from green strength and moisture. They are a
    // vendor's formulas, so derived_kpi (2026-10-01). Each is computed from ONE sample, never from window averages, and a
    // lab's value is not pooled with a controller's of the same name without a bias check.
    { name: "working_bond", unit: "%", value: 4, range: [2, 7], dataKind: "derived_kpi", paramClass: "sand_property" },
    { name: "available_bond", unit: "%", value: 6, range: [3, 10], dataKind: "derived_kpi", paramClass: "sand_property" },
    // The temperature of the sample as it is drawn, at the sample point it comes from (the muller, the prepared bunker,
    // the return silo or the moulding line), the point to be recorded with the reading (no field carries it yet; #1118
    // is open for it). Not sand_temp_out, the sand cooler's own probe in its discharge (std_sand_cooler),
    // which reads the sand leaving the cooler, not a sample at a lab.
    { name: "sand_temperature", unit: "°C", value: 35, range: [25, 45], dataKind: "analog_scalar", paramClass: "temperature" },
    // Surface abrasion (AFS 2248): the mass a rammed specimen loses in a turning screen. Above about 10 % the mould
    // face erodes and sheds sand into the metal.
    { name: "friability", unit: "%", value: 6, range: [2, 10], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "green_shear_strength", unit: "g/cm²", value: 400, range: [250, 600], dataKind: "analog_scalar", paramClass: "sand_property" },
    // A specimen dried near 110 °C before it is crushed (AFS 5202, dried).
    { name: "dry_compression_strength", unit: "g/cm²", value: 3500, range: [1500, 6000], dataKind: "analog_scalar", paramClass: "sand_property" },
    // The condensation zone, the over-wet layer behind a heated mould face (AFS 2206): its strength resists scabs.
    { name: "wet_tensile_strength", unit: "g/cm²", value: 30, range: [20, 50], dataKind: "analog_scalar", paramClass: "sand_property" },
    // Volatile matter: the mass a dried sample loses when heated without air, what is left to lay lustrous carbon at the
    // mould face. The result depends on the temperature, the hold time, a covered or an open crucible and whether the
    // clay water is driven off, so the method is to be recorded with each reading (no field carries it yet) and readings
    // by two methods are never pooled. The range is indicative; a plant sets its own.
    { name: "volatiles", unit: "%", value: 1.5, range: [0.5, 3], dataKind: "analog_percentage", paramClass: "sand_property" },
    // Carbon in the sand by combustion: sea coal, coke and burnt binder.
    { name: "total_carbon", unit: "%", value: 2, range: [0.5, 4], dataKind: "analog_percentage", paramClass: "sand_property" },
    // pH of a water extract (AFS 5113). Bentonite sand runs alkaline; acid core binders pull it down.
    { name: "ph", unit: "pH", value: 9.2, range: [8, 10.5], dataKind: "analog_scalar", paramClass: "sand_property" },
    // Ammonia nitrogen-bearing core binders leave in the sand as they break down, a source of nitrogen pinholes. In mg of
    // ammonium nitrogen per kg of DRY sand (not "ppm", which names neither the species nor the basis). The band is
    // provisional: for display only, unsourced, and no limit; a plant sets its own once it has readings. A lower bound of
    // 0 is a non-detect, not a reading of zero.
    { name: "ammoniacal_nitrogen", unit: "mg NH4-N/kg dry sand", value: 30, range: [0, 100], dataKind: "analog_scalar", paramClass: "sand_property" },
    // Mass of the rammed AFS standard specimen, 2 in by 2 in (50.8 mm, not 50 mm: a 50 mm specimen weighs about 5 % less,
    // about 7 g, a fifth of the band), a check on bulk density and flowability. In kg, not g: ingest rewrites every mass
    // to kg (platform-core/internal/units), so a lab's 155 g is stored as 0.155 kg. Shown to at least three decimals (the
    // gram): at two it moves in 10 g steps, coarser than the band.
    { name: "specimen_weight", unit: "kg", value: 0.155, range: [0.14, 0.175], dataKind: "analog_scalar", paramClass: "sand_property" },
    // Derived, so derived_kpi as carbon_equivalent is: working bond over available bond, how much of the clay the
    // muller has put to work, which the lab computes from green strength, moisture and compactability. Computed from one
    // sample, never from window averages.
    { name: "mulling_efficiency", unit: "%", value: 67, range: [50, 95], dataKind: "derived_kpi", paramClass: "sand_property" },
    // Derived: compactability over moisture, the standard temper check of a green sand (about 10.5 to 12 in iron).
    // Computed from one sample, never from window averages.
    { name: "compactibility_moisture_ratio", unit: ":1", value: 11, range: [10, 13], dataKind: "derived_kpi", paramClass: "sand_property" },
  ],
};

/** VCAT / Lab-in-a-Box ("Black Box") — all-in-one inline tester on the
 *  moulding-ready sand sampled at the moulding-machine hopper. A carve-out for
 *  testing, same reasoning as ENT_STD_SAND_LAB above (#698). STD. */
const ENT_STD_VCAT: EntityDef = {
  id: "ent-std-vcat",
  templateId: "foundry.std_vcat_black_box",
  class: "INSTRUMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine: "An all-in-one inline sand tester (VCAT) on the sand in the moulding hopper.",
  category: "STD",
  inputPorts: [{ id: "in_sample", name: "moulding_sand_sample", materialClass: "test_specimen" }],
  outputPorts: [{ id: "out_sample", name: "analyzed_sample", materialClass: "test_specimen" }],
  parameters: [
    { name: "compactibility", unit: "%", value: 42, range: [35, 50], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "permeability_number", unit: "AFS", value: 120, range: [80, 180], dataKind: "analog_scalar", paramClass: "sand_property" },
    { name: "green_compression_strength", unit: "g/cm²", value: 1500, range: [800, 2000], dataKind: "analog_scalar", paramClass: "sand_property" },
    { name: "moisture_content", unit: "%", value: 3.8, range: [3.0, 4.2], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "afs_grain_fineness", unit: "AFS", value: 55, range: [45, 70], dataKind: "analog_scalar", paramClass: "sand_property" },
    { name: "active_clay_content", unit: "%", value: 9, range: [5, 13], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "loi", unit: "%", value: 3.5, range: [1, 7], dataKind: "analog_percentage", paramClass: "sand_property" },
  ],
};

/** Template id of the AFS sieve shaker — referenced by the Add-equipment wizard
 *  to auto-provision its datapoints. */
export const SIEVE_SHAKER_TEMPLATE_ID = "foundry.std_sieve_shaker";

/** AFS sieve shaker — a lab sieve stack producing a Grain Fineness Number (GFN)
 *  from the mass retained on each standard AFS mesh. Its 12 per-mesh retained-mass
 *  datapoints + a GFN computed tag are provisioned as MANUAL-entry tags (see
 *  lib/plant/provision-sieve.ts), NOT declared here as parameters: a parameter with
 *  a dataKind would be seeded as a simulated gateway tag by the Add-equipment wizard
 *  and collide with the hand-keyed capture. Parameters stay empty so the machine's
 *  data comes purely from Data-entry sieve captures. STD. */
const ENT_STD_SIEVE_SHAKER: EntityDef = {
  id: "ent-std-sieve-shaker",
  templateId: SIEVE_SHAKER_TEMPLATE_ID,
  class: "INSTRUMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine: "Measures the grain fineness of the sand (AFS number) from what each sieve holds back, with the results keyed in on Data entry.",
  category: "STD",
  inputPorts: [{ id: "in_sample", name: "green_sand_sample", materialClass: "green_sand" }],
  outputPorts: [{ id: "out_sample", name: "analyzed_sample", materialClass: "green_sand" }],
  parameters: [],
};

// --- Core shop (cold-box; cores tracked as parts) ----------------------------

/** Raw core sand store — washed silica core sand source. STD. */
const ENT_STD_CORE_SAND_STORE: EntityDef = {
  id: "ent-std-core-sand-store",
  templateId: "foundry.std_raw_core_sand_store",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Washed and dried silica sand for the core shop.",
  category: "STD",
  inputPorts: [{ id: "in_sand", name: "core_sand", materialClass: "core_sand" }],
  outputPorts: [{ id: "out_sand", name: "core_sand", materialClass: "core_sand" }],
  parameters: [{ name: "level", unit: "%", value: 70, range: [0, 100], dataKind: "analog_percentage" }],
};

/** Resin store (PUR 1+2) — temperature-controlled cold-box binder tanks. STD. */
const ENT_STD_RESIN_STORE: EntityDef = {
  id: "ent-std-resin-store",
  templateId: "foundry.std_resin_store",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Temperature controlled tanks of the two-part polyurethane resin that binds cold-box cores.",
  category: "STD",
  inputPorts: [{ id: "in_resin", name: "resin_pur", materialClass: "resin_pur" }],
  outputPorts: [{ id: "out_resin", name: "resin_pur", materialClass: "resin_pur" }],
  parameters: [{ name: "temperature", unit: "°C", value: 22, range: [18, 28], dataKind: "analog_scalar" }],
};

/** Core sand mixer (continuous) — blends core sand + PUR resin. STD. */
const ENT_STD_CORE_SAND_MIXER: EntityDef = {
  id: "ent-std-core-sand-mixer",
  templateId: "foundry.std_core_sand_mixer",
  class: "EQUIPMENT",
  lotOp: "MERGE",
  iconKey: "cog",
  oneLine: "Blends core sand and polyurethane resin into a core mix, continuously.",
  category: "STD",
  flowMode: "continuous",
  // WS-B#9b — the three dossier numbers for a core-sand mixer, all STATED values
  // (02-sand-core.md:266 "Mix rate | t/h | 20 [5-45]", :267 "22 [11-75]" kW, :268
  // "Bench / work life | min | 30 [10-90]"). This template is the sibling of
  // foundry.nobake_continuous_mixer, which already ships the same three keys, so
  // this closes an inconsistency rather than starting a convention.
  //
  // `bench_life_min` DUPLICATES the `bench_life` parameter below (same 30, same
  // min). That is the WS-B#8 two-homes-for-one-quantity defect, and it is NOT
  // resolved here: deleting a data port can orphan a customer's tag binding, so it
  // needs the same staging orphan-check WS-B#9a ran before retiring `rated_power`.
  // Recorded as a measured follow-up in _STATUS, not silently reconciled. The
  // nobake sibling shows the intended end state: bench life is a RATING, no port.
  ratedProperties: [
    { name: "flow_rate_t_per_h", value: 20, unit: "t/h" },
    { name: "rated_power_kw", value: 22, unit: "kW" },
    { name: "bench_life_min", value: 30, unit: "min" },
  ],
  inputPorts: [
    { id: "in_sand", name: "core_sand", materialClass: "core_sand" },
    { id: "in_resin", name: "resin_pur", materialClass: "resin_pur" },
  ],
  outputPorts: [{ id: "out_mix", name: "core_mix", materialClass: "core_sand" }],
  parameters: [
    { name: "resin_pct", unit: "%", value: 1.2, range: [0.8, 2.0], dataKind: "analog_percentage" },
    { name: "mix_ratio", unit: ":1", value: 1, range: [0.5, 2], dataKind: "analog_scalar" },
    { name: "bench_life", unit: "min", value: 30, range: [10, 90], dataKind: "analog_scalar" },
  ],
};

/** Core shooter / blower — blows + amine-gasses the core; PART producer (CR). STD. */
const ENT_STD_CORE_SHOOTER: EntityDef = {
  id: "ent-std-core-shooter",
  templateId: "foundry.std_core_shooter",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Blows core sand into the core box and cures it with amine gas into a cold-box core.",
  category: "STD",
  flowMode: "batch",
  ratedProperties: [
    { name: "cavities_per_box", value: 4 },
    { name: "rated_power_kw", value: 30, unit: "kW" },
  ],
  inputPorts: [
    { id: "in_sand", name: "core_mix", materialClass: "core_sand" },
    { id: "in_amine", name: "amine_catalyst", materialClass: "amine_catalyst" },
  ],
  outputPorts: [{ id: "out_core", name: "cold_box_core", materialClass: "cold_box_core" }],
  parameters: [
    { name: "shoot_pressure", unit: "bar", value: 4, range: [2, 6], dataKind: "analog_scalar" },
    { name: "cure_time", unit: "s", value: 15, range: [5, 30], dataKind: "analog_scalar" },
    { name: "amine_dose", unit: "g", value: 8, range: [1, 20], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_core_made", name: "core_made", purpose: "pulse", dataKind: "count_scalar" }],
  trackedOutput: {
    tracked: true,
    idPrefix: "CR",
    label: "Core",
    isPart: true,
    qc: { tests: ["core_strength", "core_gas_evolution", "dimensional"], defects: ["blowholes", "breakage", "veining"] },
  },
};

/** Core knockout / deflash — strips flash/fins from the shot core. STD. */
const ENT_STD_CORE_KNOCKOUT: EntityDef = {
  id: "ent-std-core-knockout",
  templateId: "foundry.std_core_knockout",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "clipboard",
  oneLine: "Removes the flash and parting fins from the blown core.",
  category: "STD",
  inputPorts: [{ id: "in_core", name: "cold_box_core", materialClass: "cold_box_core" }],
  outputPorts: [{ id: "out_core", name: "cold_box_core", materialClass: "cold_box_core" }],
  parameters: [{ name: "deflash_count", value: 1, dataKind: "count_scalar" }],
};

/** Core coating & dry — refractory dip/spray + oven dry. STD. */
const ENT_STD_CORE_COATING: EntityDef = {
  id: "ent-std-core-coating",
  templateId: "foundry.std_core_coating",
  class: "EQUIPMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "cog",
  oneLine: "Dips or sprays the core with a refractory wash and dries it in an oven.",
  category: "STD",
  inputPorts: [{ id: "in_core", name: "cold_box_core", materialClass: "cold_box_core" }],
  outputPorts: [{ id: "out_core", name: "cold_box_core", materialClass: "cold_box_core" }],
  parameters: [
    { name: "coat_density", unit: "Bé", value: 38, range: [25, 50], dataKind: "analog_scalar" },
    { name: "dry_temp", unit: "°C", value: 150, range: [80, 200], dataKind: "analog_scalar" },
  ],
};

/** Core storage / buffer — racked cores awaiting setting. STD. */
const ENT_STD_CORE_STORAGE: EntityDef = {
  id: "ent-std-core-storage",
  templateId: "foundry.std_core_storage",
  class: "TRANSPORT",
  lotOp: "PASSTHROUGH",
  iconKey: "truck",
  oneLine: "Holds the cold-box cores on racks, a core buffer until mould assembly.",
  category: "STD",
  ratedProperties: [{ name: "core_shelf_life_h", value: 120, unit: "h" }],
  inputPorts: [{ id: "in_core", name: "cold_box_core", materialClass: "cold_box_core" }],
  outputPorts: [{ id: "out_core", name: "cold_box_core", materialClass: "cold_box_core" }],
  parameters: [{ name: "level", unit: "%", value: 55, range: [0, 100], dataKind: "analog_percentage" }],
};

/** Core lab — samples cores for gas evolution + strength. Non-consuming leaf. STD. */
const ENT_STD_CORE_LAB: EntityDef = {
  id: "ent-std-core-lab",
  templateId: "foundry.std_core_lab",
  class: "INSTRUMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine: "The core lab that tests core gas evolution and cured core strength.",
  category: "STD",
  inputPorts: [{ id: "in_sample", name: "core_sample", materialClass: "cold_box_core" }],
  outputPorts: [{ id: "out_sample", name: "analyzed_sample", materialClass: "cold_box_core" }],
  parameters: [
    { name: "core_gas_evolution", unit: "ml/g", value: 14, range: [5, 25], dataKind: "analog_scalar" },
    { name: "core_strength", unit: "N/cm²", value: 280, range: [150, 400], dataKind: "analog_scalar" },
  ],
};

// --- Versatile BLE handhelds --------------------------------------------------
// The three ESP32-S3 testers (hardware/ACS_Unified). Unlike every other instrument
// here, their value arrives from a PHONE over Bluetooth — `bleModel` tells
// generatePlant to emit no simulated gateway and the server to provision a
// source='ble' tag instead. A probe with nobody pressing it therefore has NO data,
// and the empty state is the truth.
//
// Shape notes, each load-bearing:
//   * INPUT PORT ONLY. The handheld is pressed against a mould/core that stays on
//     the line — nothing flows through it. That makes it a genealogy "measurement
//     leaf" (genealogy_newmodel.go classifyRole: "never in the casting chain"),
//     which is exactly right. It also means e.OutMaterial is empty, so the
//     classifier must match on the NAME — hence the names below spell out the
//     measured property, and measuresSandName carries "core hardness" for the VCH.
//   * The names MUST contain "Versatile" AND the property. genealogy_newmodel.go
//     (measuresSandName:247, measuresMouldName:268) keys on those words to classify
//     these as measurement leaves; rename them and they silently become process
//     nodes in the genealogy.
//   * Param name/unit/range MUST match platform-core/internal/twin/handheld_probes.go
//     `handheldCatalog`. The server derives the real tag from ITS catalog, so a
//     drift here only desyncs the canvas chip from the tag — silently.

const versatileHandheld = (
  id: string,
  templateId: string,
  model: "VCH" | "VMH" | "VMS",
  oneLine: string,
  inPort: PortDef,
  param: ParameterDef,
): EntityDef => ({
  id,
  templateId,
  class: "INSTRUMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine,
  category: "STD",
  bleModel: model,
  inputPorts: [inPort],
  outputPorts: [], // measurement leaf — the sample never leaves the line
  parameters: [param],
});

/** Versatile VCH — handheld core hardness. Reads a CURED core (cold_box_core), not
 *  core_sand: there is nothing to indent until the shooter cures it. */
const ENT_VCH: EntityDef = versatileHandheld(
  "ent-vch",
  "foundry.versatile_vch",
  "VCH",
  "A handheld core hardness tester whose reading the app captures over Bluetooth.",
  { id: "in_core", name: "core_sample", materialClass: "cold_box_core" },
  { name: "core_hardness", unit: "AFS", value: 85, range: [70, 95], dataKind: "analog_scalar" },
);

/** Versatile VMH — handheld mould hardness. Reads an UNPOURED mould: once metal is
 *  in the cavity you are no longer reading the sand surface. */
const ENT_VMH: EntityDef = versatileHandheld(
  "ent-vmh",
  "foundry.versatile_vmh",
  "VMH",
  "A handheld mould hardness tester whose reading the app captures over Bluetooth.",
  { id: "in_mould", name: "mould_sample", materialClass: "green_sand_mould" },
  { name: "mould_hardness", unit: "AFS", value: 82, range: [70, 95], dataKind: "analog_scalar" },
);

/** Versatile VMS — handheld mould strength. Unit is the device's own kg/cm2 as it
 *  reads on the OLED — deliberately NOT the pack VMSD's g/cm² [500,2000]. Same
 *  quantity, 1000x apart; keeping the device's scale means no silent arithmetic
 *  between the operator's screen and the dashboard. `kg/cm2` is ASCII on purpose:
 *  it must byte-match handheldCatalog["VMS"].Unit, so do NOT "tidy" it to kg/cm²
 *  the way the neighbouring bench testers are written. */
const ENT_VMS: EntityDef = versatileHandheld(
  "ent-vms",
  "foundry.versatile_vms",
  "VMS",
  "A handheld mould strength tester whose reading the app captures over Bluetooth.",
  { id: "in_mould", name: "mould_sample", materialClass: "green_sand_mould" },
  { name: "mould_strength", unit: "kg/cm2", value: 1.2, range: [0, 2], dataKind: "analog_scalar" },
);

// --- Mould assembly / pour / finishing ---------------------------------------

/** Core setting (mould assembly) — sets cores into the green-sand mould. The
 *  core→mould/casting link (linked_casting_id) is stamped by the emitter. STD. */
const ENT_STD_CORE_SETTING: EntityDef = {
  id: "ent-std-core-setting",
  templateId: "foundry.std_core_setting",
  class: "STATION",
  lotOp: "MERGE",
  iconKey: "clipboard",
  oneLine: "Sets each cold-box core into the green sand mould before the mould is closed.",
  category: "STD",
  // THE COMMENT THAT USED TO BE HERE SAID `cores_per_mould` was "read by the emitter".
  // It was not, at any value, on any entity — verified 2026-08-02, zero live Go readers.
  // It is the false claim the whole Connectivity Build was organised around killing, and
  // WS-C#2 closed it by RETIRING the key rather than by wiring it (council ruling §5.3(1)):
  // a heat's core count is DERIVED (cores_per_casting × cavities_per_box) and its ONE
  // authoritative home is the pattern↔core-box link table. A rated key here was a third
  // home for a number that already had two, and they differ by 4× on a 4-cavity box.
  //
  // So this station ships NO rated core count. What a heat's Core Setting step now shows
  // is the count the emitter resolved (`cores_made`) with its provenance (`cores_basis`),
  // and — when nothing declares a core weight — an explicit withhold instead of a number.
  // Re-adding the name to ratedProperties[] fails the Go build
  // (TestConnectivityStatic_RetiredKeysStayRetired).
  ratedProperties: [],
  inputPorts: [
    { id: "in_mould", name: "green_sand_mould", materialClass: "green_sand_mould" },
    { id: "in_core", name: "cold_box_core", materialClass: "cold_box_core" },
  ],
  outputPorts: [{ id: "out_mould", name: "cored_mould", materialClass: "green_sand_mould" }],
  parameters: [],
};

/**
 * Pouring by hand from pour ladles (sweep PR-E, 2026-10-24, H3). A treatment ladle fills pour ladles, and people pour
 * the moulds from them, with no pouring machine: the line the Standard Green-Sand Foundry now builds. The plant reads the
 * pour temperature in the first and the last pour ladle a treatment ladle fills, or once where its place in the pour is
 * not known, and the first mould car a ladle pours on a car loop. Its template is the pack's hand-written
 * foundry.manual_pour_station (the same material ports, these readings among its sensors). A line with a pouring machine
 * adds the Auto-Pour Station (ENT_POUR) instead, a separate stage.
 */
const ENT_MANUAL_POUR: EntityDef = {
  id: "ent-manual-pour",
  templateId: "foundry.manual_pour_station",
  class: "EQUIPMENT",
  lotOp: "SPLIT",
  iconKey: "cog",
  oneLine: "Pours the moulds by hand from pour ladles that a treatment ladle fills.",
  category: "STD",
  inputPorts: [
    { id: "in_metal", name: "molten_metal", materialClass: "molten_iron" },
    { id: "in_moulds", name: "moulds", materialClass: "green_sand_mould" },
  ],
  outputPorts: [{ id: "out_poured", name: "poured_moulds", materialClass: "poured_mould" }],
  parameters: [
    { name: "pour_temperature_first", unit: "°C", value: 1400, range: [1340, 1450], dataKind: "analog_scalar" },
    { name: "pour_temperature_last", unit: "°C", value: 1375, range: [1340, 1450], dataKind: "analog_scalar" },
    { name: "pour_temperature_single", unit: "°C", value: 1390, range: [1340, 1450], dataKind: "analog_scalar" },
    { name: "first_car", value: 1, range: [1, 200], dataKind: "count_scalar" },
  ],
  // No machine signals: a hand pour has no pour-start coil or casting pulse to read (the Auto-Pour Station's), only
  // these readings, as packs/foundry/entities/manual_pour_station.yaml has them.
};

/** De-gating — knocks runners/risers off the raw casting (→ returns). STD. */
const ENT_STD_DEGATING: EntityDef = {
  id: "ent-std-degating",
  templateId: "foundry.std_degating",
  class: "EQUIPMENT",
  lotOp: "SPLIT",
  iconKey: "cog",
  oneLine: "Knocks the runners and risers off the raw casting, and they go back into the charge.",
  category: "STD",
  flowMode: "discrete_unit",
  inputPorts: [{ id: "in_castings", name: "raw_castings", materialClass: "raw_casting" }],
  outputPorts: [
    { id: "out_castings", name: "raw_castings", materialClass: "raw_casting" },
    // #677: internal-return class (not the legacy ferrous_metal umbrella) —
    // feeds ENT_CHARGE_PREP.in_returns, never charge's bought-metal ports.
    { id: "out_returns", name: "runners_risers", materialClass: "foundry_return" },
  ],
  parameters: [{ name: "gating_yield", unit: "%", value: 75, range: [50, 90], dataKind: "analog_percentage" }],
};

/** Shot blasting — wheelblast clean of the casting surface. STD. */
const ENT_STD_SHOT_BLAST: EntityDef = {
  id: "ent-std-shot-blast",
  templateId: "foundry.std_shot_blasting",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Shot blast wheels clean the scale and leftover sand off the castings.",
  category: "STD",
  flowMode: "discrete_unit",
  inputPorts: [{ id: "in_castings", name: "raw_castings", materialClass: "raw_casting" }],
  outputPorts: [{ id: "out_castings", name: "blasted_castings", materialClass: "raw_casting" }],
  parameters: [
    { name: "shot_flow", unit: "kg/min", value: 180, range: [100, 300], dataKind: "analog_scalar" },
    { name: "time", unit: "min", value: 6, range: [2, 15], dataKind: "analog_scalar" },
  ],
};

/** Fettling / grinding — grind gate stubs + parting lines. STD. */
const ENT_STD_FETTLING: EntityDef = {
  id: "ent-std-fettling",
  templateId: "foundry.std_fettling",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Dresses the gate stubs and grinds the parting lines smooth.",
  category: "STD",
  flowMode: "discrete_unit",
  inputPorts: [{ id: "in_castings", name: "blasted_castings", materialClass: "raw_casting" }],
  outputPorts: [{ id: "out_castings", name: "fettled_castings", materialClass: "raw_casting" }],
  parameters: [{ name: "grind_time", unit: "min", value: 8, range: [2, 30], dataKind: "analog_scalar" }],
};

/** Final inspection + NDT + micro — accept/reject + defect attribution. The
 *  rejected stream returns to charge as an internal-return credit (#677 —
 *  casting_rejects, not the legacy ferrous_metal umbrella). STD. */
const ENT_STD_FINAL_INSPECTION: EntityDef = {
  id: "ent-std-final-inspection",
  templateId: "foundry.std_final_inspection",
  class: "STATION",
  lotOp: "SPLIT",
  iconKey: "clipboard",
  oneLine: "Inspects each casting by eye, by non-destructive testing (NDT) and by its microstructure, accepts or rejects it, and records its defects.",
  category: "STD",
  inputPorts: [{ id: "in_castings", name: "fettled_castings", materialClass: "raw_casting" }],
  outputPorts: [
    { id: "out_accepted", name: "finished_castings", materialClass: "finished_casting" },
    { id: "out_specimen", name: "test_specimen", materialClass: "test_specimen" },
    // #677: internal-return class (not the legacy ferrous_metal umbrella) —
    // feeds ENT_CHARGE_PREP.in_rejects, never charge's bought-metal ports.
    { id: "out_rejected", name: "rejected_castings", materialClass: "casting_rejects" },
  ],
  parameters: [
    { name: "defect_count", value: 4, dataKind: "count_scalar" },
    { name: "rejection_rate", unit: "%", value: 4, range: [0, 20], dataKind: "analog_percentage" },
    // WS-D / MET §3: nodularity is a per-LADLE micrographic measurement. The nominal 90 shipped
    // here is what produced the live defect measured on staging — three `nodularity` chips of
    // 75.6 / 82.x / 85.3 % (each with this [70,100] band and a pre-QB5 inSpec verdict) on a GREY
    // IRON plant, which has no nodular graphite to measure at all. Port + range kept, value gone.
    { name: "nodularity", unit: "%", range: [70, 100], dataKind: "analog_percentage" },
    { name: "hardness", unit: "BHN", value: 200, range: [130, 300], dataKind: "analog_scalar" },
  ],
};

/** Finished casting store — despatch sink for accepted castings. STD. */
const ENT_STD_FINISHED_STORE: EntityDef = {
  id: "ent-std-finished-store",
  templateId: "foundry.std_finished_casting_store",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Holds the accepted, inspected castings until they are dispatched.",
  category: "STD",
  inputPorts: [{ id: "in_castings", name: "finished_castings", materialClass: "finished_casting" }],
  outputPorts: [],
  parameters: [{ name: "dispatch_ready", value: 85, unit: "count", dataKind: "count_scalar" }],
};

/** Belt conveyor — moves return sand (or moulds and castings) between stations.
 *  Added 2026-09-24 (plan 2026-09-24, W-A): plants log conveyor speeds and running
 *  states, and without a template those columns had nowhere to land. Ports carry
 *  return sand, the commonest monitored case; a plant re-points them per instance. */
const ENT_CONVEYOR: EntityDef = {
  id: "ent-conveyor",
  templateId: "foundry.conveyor",
  class: "TRANSPORT",
  lotOp: "PASSTHROUGH",
  iconKey: "truck",
  oneLine: "A belt conveyor that moves return sand or castings between stations, with its speed and whether it is running.",
  inputPorts: [{ id: "in_material", name: "return_sand", materialClass: "return_sand" }],
  outputPorts: [{ id: "out_material", name: "return_sand", materialClass: "return_sand" }],
  parameters: [
    { name: "speed", unit: "m/min", value: 30, range: [0, 150], dataKind: "analog_scalar", paramClass: "rate" },
    { name: "running", value: 1, dataKind: "boolean_state", paramClass: "state" },
  ],
};

// ── Catalog ───────────────────────────────────────────────────────────────

export const STAGE_LIBRARY: Record<string, StageTemplate> = {
  // ── Archetype A: iron green-sand line (reuses canonical defs) ──
  charge: { key: "charge", label: "Charge Prep Bay", def: ENT_CHARGE_PREP },
  sand: { key: "sand", label: "Sand Mixer", def: ENT_SAND_MIXER },
  furnace: { key: "furnace", label: "Induction Furnace", def: ENT_FURNACE },
  spectro: { key: "spectro", label: "OES Spectrometer", def: ENT_SPECTROMETER },
  moisture: { key: "moisture", label: "Moisture Tester", def: ENT_MOISTURE_TESTER },
  vccd: { key: "vccd", label: "Versatile VCCD-I", def: ENT_VCCD },
  vud: { key: "vud", label: "Versatile VUD-III", def: ENT_VUD },
  vpd: { key: "vpd", label: "Versatile VPD-II", def: ENT_VPD },
  vmbd: { key: "vmbd", label: "Versatile VMBD", def: ENT_VMBD },
  vmhd: { key: "vmhd", label: "Versatile VMHD", def: ENT_VMHD },
  moulding: { key: "moulding", label: "Moulding Line", def: ENT_MOULDING },
  pour: { key: "pour", label: "Auto-Pour Station", def: ENT_POUR },
  // Pouring by hand from pour ladles (H3): the Standard Green-Sand Foundry's pour; Auto-Pour stays its own stage.
  manual_pour: { key: "manual_pour", label: "Pouring", def: ENT_MANUAL_POUR },
  cooling: { key: "cooling", label: "Cooling Line", def: ENT_COOLING },
  shakeout: { key: "shakeout", label: "Shakeout", def: ENT_SHAKEOUT },
  qc: { key: "qc", label: "Visual / NDT Inspection", def: ENT_QC },
  tensile: { key: "tensile", label: "Tensile Testing", def: ENT_TENSILE },

  // ── Melt variants (B–H) ──
  cupola: { key: "cupola", label: "Cupola Furnace", def: ENT_CUPOLA },
  eaf: { key: "eaf", label: "Electric-Arc Furnace", def: ENT_EAF },
  aod: { key: "aod", label: "AOD Converter", def: ENT_AOD },
  crucible: { key: "crucible", label: "Crucible Furnace", def: ENT_CRUCIBLE },
  reverb: { key: "reverb", label: "Reverb Melter + Dosing", def: ENT_REVERB },
  holding: { key: "holding", label: "Ladle / Holding Furnace", def: ENT_HOLDING },

  // ── Iron treatment ──
  mg_treatment: { key: "mg_treatment", label: "Mg Treatment", def: ENT_MG_TREATMENT },
  inoculation: { key: "inoculation", label: "Inoculation", def: ENT_INOCULATION },

  // ── Melt-line: charge yard (bought-material boundary, #677) ──
  charge_yard: { key: "charge_yard", label: "Charge Yard", def: ENT_STD_CHARGE_YARD },

  // ── Sand plant: pattern shop (bought/reusable-tooling boundary, #698) ──
  std_pattern_shop: { key: "std_pattern_shop", label: "Pattern Shop", def: ENT_STD_PATTERN_SHOP },

  // ── Aluminium melt treatment ──
  degassing: { key: "degassing", label: "Degassing Station", def: ENT_DEGASSING },

  // ── No-bake sand route ──
  nobake_mixer: { key: "nobake_mixer", label: "No-bake Mixer", def: ENT_NOBAKE_MIXER },
  nobake_mould: { key: "nobake_mould", label: "No-bake Moulding Bay", def: ENT_NOBAKE_MOULD },
  mould_coat: { key: "mould_coat", label: "Mould Coat + Dry", def: ENT_MOULD_COAT },
  sand_reclaim: { key: "sand_reclaim", label: "Sand Reclamation", def: ENT_SAND_RECLAIM },
  nobake_supply_yard: { key: "nobake_supply_yard", label: "No-bake Supply Yard", def: ENT_NOBAKE_SUPPLY_YARD },

  // ── Core shop ──
  core: { key: "core", label: "Cold-box Core Machine", def: ENT_CORE },

  // ── Die casting (HPDC / gravity / LPDC) ──
  hpdc: { key: "hpdc", label: "HPDC Machine", def: ENT_HPDC },
  die_spray: { key: "die_spray", label: "Die Spray Unit", def: ENT_DIE_SPRAY },
  trim_press: { key: "trim_press", label: "Trim Press", def: ENT_TRIM_PRESS },
  gravity_die: { key: "gravity_die", label: "Gravity / LPDC Station", def: ENT_GRAVITY_DIE },

  // ── Investment casting ──
  wax_injection: { key: "wax_injection", label: "Wax Injection Press", def: ENT_WAX_INJECTION },
  pattern_assembly: { key: "pattern_assembly", label: "Pattern Assembly", def: ENT_PATTERN_ASSEMBLY },
  shell_build: { key: "shell_build", label: "Shell Build Line", def: ENT_SHELL_BUILD },
  dewax_fire: { key: "dewax_fire", label: "Dewax + Fire", def: ENT_DEWAX_FIRE },
  investment_pour: { key: "investment_pour", label: "Investment Pour", def: ENT_INVESTMENT_POUR },
  shell_knockout: { key: "shell_knockout", label: "Shell Knockout", def: ENT_SHELL_KNOCKOUT },
  cutoff: { key: "cutoff", label: "Cut-off Station", def: ENT_CUTOFF },
  centrifugal: { key: "centrifugal", label: "Centrifugal Machine", def: ENT_CENTRIFUGAL },

  // ── Finishing / post-processing ──
  shot_blast: { key: "shot_blast", label: "Shot Blast", def: ENT_SHOT_BLAST },
  fettling: { key: "fettling", label: "Fettling / Grinding", def: ENT_FETTLING },
  heat_treat: { key: "heat_treat", label: "Heat-treat Furnace", def: ENT_HEAT_TREAT },
  machining: { key: "machining", label: "CNC Machining", def: ENT_MACHINING },
  ndt: { key: "ndt", label: "NDT / Metrology Lab", def: ENT_NDT },

  // ── Standard Green-Sand Foundry (STD archetype) ──
  // Melt-line treatment
  std_treatment_ladle: { key: "std_treatment_ladle", label: "Mg / Treatment Ladle", def: ENT_STD_TREATMENT_LADLE },
  std_inoculation_ladle: { key: "std_inoculation_ladle", label: "Inoculation Ladle", def: ENT_STD_INOCULATION_LADLE },
  // Sand plant: stores / hoppers / bunkers
  std_raw_material_store: { key: "std_raw_material_store", label: "Raw Material Store", def: ENT_STD_RAW_MATERIAL_STORE },
  std_batch_hopper: { key: "std_batch_hopper", label: "Batch Hopper", def: ENT_STD_BATCH_HOPPER },
  std_prepared_bunker: { key: "std_prepared_bunker", label: "Prepared Sand Bunker", def: ENT_STD_PREPARED_BUNKER },
  std_moulding_hopper: { key: "std_moulding_hopper", label: "Moulding Machine Hopper", def: ENT_STD_MOULDING_HOPPER },
  // Sand reclamation loop
  std_polygon_sieve: { key: "std_polygon_sieve", label: "Polygon Sieving / Lump Crusher", def: ENT_STD_POLYGON_SIEVE },
  std_magnetic_separator: { key: "std_magnetic_separator", label: "Magnetic Separator", def: ENT_STD_MAGNETIC_SEPARATOR },
  std_sand_cooler: { key: "std_sand_cooler", label: "Sand Cooler", def: ENT_STD_SAND_COOLER },
  std_return_silo: { key: "std_return_silo", label: "Return Sand Silo", def: ENT_STD_RETURN_SILO },
  std_dust_extractor: { key: "std_dust_extractor", label: "Dust Extractor", def: ENT_STD_DUST_EXTRACTOR },
  std_dust_storage: { key: "std_dust_storage", label: "Dust Storage", def: ENT_STD_DUST_STORAGE },
  std_compactability_controller: { key: "std_compactability_controller", label: "Compactability Controller", def: ENT_STD_COMPACTABILITY_CONTROLLER },
  // Sand lab + VCAT
  std_sand_lab: { key: "std_sand_lab", label: "Sand Laboratory", def: ENT_STD_SAND_LAB },
  std_vcat: { key: "std_vcat", label: "VCAT / Lab-in-a-Box", def: ENT_STD_VCAT },
  std_sieve_shaker: { key: "std_sieve_shaker", label: "AFS Sieve Shaker", def: ENT_STD_SIEVE_SHAKER },
  // Core shop
  std_core_sand_store: { key: "std_core_sand_store", label: "Raw Core Sand Store", def: ENT_STD_CORE_SAND_STORE },
  std_resin_store: { key: "std_resin_store", label: "Resin Store", def: ENT_STD_RESIN_STORE },
  std_core_sand_mixer: { key: "std_core_sand_mixer", label: "Core Sand Mixer", def: ENT_STD_CORE_SAND_MIXER },
  std_core_shooter: { key: "std_core_shooter", label: "Core Shooter", def: ENT_STD_CORE_SHOOTER },
  std_core_knockout: { key: "std_core_knockout", label: "Core Knockout / Deflash", def: ENT_STD_CORE_KNOCKOUT },
  std_core_coating: { key: "std_core_coating", label: "Core Coating & Dry", def: ENT_STD_CORE_COATING },
  std_core_storage: { key: "std_core_storage", label: "Core Storage", def: ENT_STD_CORE_STORAGE },
  std_core_lab: { key: "std_core_lab", label: "Core Lab", def: ENT_STD_CORE_LAB },

  // ── Versatile BLE handhelds (phone-captured; no simulated gateway) ──
  // The labels MUST keep "Versatile" AND the measured property: genealogy_newmodel.go
  // classifies these as measurement leaves by NAME (measuresSandName:247 /
  // measuresMouldName:268), and with no output port there is no material class to
  // fall back on. Rename them and they silently stop reaching the genealogy.
  vch: { key: "vch", label: "Versatile VCH · Core Hardness", def: ENT_VCH },
  vmh: { key: "vmh", label: "Versatile VMH · Mould Hardness", def: ENT_VMH },
  vms: { key: "vms", label: "Versatile VMS · Mould Strength", def: ENT_VMS },
  // Mould assembly / pour / finishing
  std_core_setting: { key: "std_core_setting", label: "Core Setting", def: ENT_STD_CORE_SETTING },
  std_degating: { key: "std_degating", label: "De-gating", def: ENT_STD_DEGATING },
  std_shot_blast: { key: "std_shot_blast", label: "Shot Blasting", def: ENT_STD_SHOT_BLAST },
  std_fettling: { key: "std_fettling", label: "Fettling / Grinding", def: ENT_STD_FETTLING },
  std_final_inspection: { key: "std_final_inspection", label: "Final Inspection + NDT", def: ENT_STD_FINAL_INSPECTION },
  std_finished_store: { key: "std_finished_store", label: "Finished Casting Store", def: ENT_STD_FINISHED_STORE },
  // Material handling
  conveyor: { key: "conveyor", label: "Conveyor", def: ENT_CONVEYOR },
};
